import { createEffect, createMemo, createSignal, onMount, onCleanup, Show, untrack } from "solid-js";
import * as d3 from "d3";
import { computeScatterFitValues } from "./scatterRegressionTask";

import { lasso } from "../../utils/d3-lasso";
import { formatDateTime, putData } from "../../utils/global";
import {
  resolveDataField,
  EMPTY_GROUP_COLOR,
  isEmptyGroupValue,
  isRaceColorMode,
  isTrainingRaceMetadataValue,
  RACE_NUMBER_COLLAPSED_LEGEND_NAME,
  PERFORMANCE_DENSITY_COLOR,
  PERFORMANCE_VMG_COLOR,
  isPerformanceRampColor,
} from "../../utils/colorScale";
import { densityHeatColor, vmgHeatColor } from "../../utils/scatterColors";
import { pointDensityFractions } from "../../utils/scatterDensity";
import { vmgFractions, vmgPercFromRow } from "../../utils/scatterVmgPerc";
import { performanceDisplayStore } from "../../store/performanceDisplayStore";
import { error as logError, debug as logDebug } from "../../utils/console";
import {
    buildBaselineSegments,
    legKeyFromUwDw,
    type BaselineOverlay,
    type BaselineSegment,
} from "../../utils/baselineCurve";
import { sampleScatterForRender, type ScatterRenderPoint } from "../../utils/scatterRenderSample";
import {
    shouldUseHighDensityCanvasScatter,
    findNearestScatterPoint,
    buildLassoScreenPoints,
    CANVAS_HIT_RADIUS_PX,
} from "../../utils/advancedScatterHitTest";
import {
    buildRecencyOpacityScale,
    resolveScatterPointOpacity,
    resolveScatterPointRecencySizeBonus,
    type RecencyOpacityScale,
} from "../../utils/livePerformancePointOpacity";
import { apiEndpoints } from "../../config/env";

// Static import for worker - Vite will bundle this correctly in production
import LassoSelectionWorker from "../../workers/lasso-selection-processor.ts?worker";

import { setTooltip } from "../../store/globalStore";

/** One reference curve (a baseline overlay) as drawn, for the Highlight Fits & Targets side table. */
interface ReferenceCurve {
    name: string;
    color: string;
    points: Array<{ X: number; Y: number }>;
}

/** Color By DENSITY grid cell (px). */
const DENSITY_CELL_PX = 14;

/**
 * Display Options → Highlight Fits & Targets: point size and alpha multipliers. The canvas path
 * applies them here; the SVG path gets the same values from `.advanced-scatter-highlight-lines`
 * in Styles.css — keep the two in step.
 */
const HIGHLIGHT_LINES_POINT_SCALE = 0.55;
const HIGHLIGHT_LINES_POINT_ALPHA = 0.3;
import { selectedEvents, setSelectedEvents, selectedAggregateEvents, setSelectedAggregateEvents, highlightedEventIds, highlightedAggregateEventIds, triggerSelection, setTriggerSelection, setHasSelection, clearSelection, isEventHidden, type SelectionDomain } from "../../store/selectionStore";
import { selectedStatesTimeseries, selectedRacesTimeseries, selectedLegsTimeseries, selectedGradesTimeseries } from "../../store/filterStore";
import { isDark, theme, getStrokeColor as getThemeStrokeColor } from "../../store/themeStore";
import { user } from "../../store/userStore";
import { persistantStore } from "../../store/persistantStore";
import { defaultChannelsStore } from "../../store/defaultChannelsStore";
import { unifiedDataStore } from "../../store/unifiedDataStore";
import { huniDBStore } from "../../store/huniDBStore";
import { shouldBlockGradeLikeHotkeysForPerformanceSelections } from "../../utils/performanceLocalSelection";

import Table from "./FitTable";
import infoIconUrl from "../../assets/info.svg";
import warningIconUrl from "../../assets/warning.svg";

/** Matches `Styles.css` — wait cursor while SELECTIONS digit hotkey work settles */
const PERFORMANCE_SELECTION_HOTKEY_BUSY_CLASS = "performance-selection-hotkey-busy";

/** Matches `Styles.css` — wait cursor while lasso polygon selection is computed in a worker */
const LASSO_COMPUTING_CLASS = "advanced-scatter-lasso-computing";

function setLassoComputingCursor(on: boolean): void {
    if (typeof document === "undefined") return;
    document.documentElement.classList.toggle(LASSO_COMPUTING_CLASS, on);
}

/** Matches `Styles.css` — wait cursor while scatter color-by / groups update (draw or transition) */
const COLOR_RELOADING_CLASS = "advanced-scatter-color-reloading";

const SCATTER_COLOR_TRANSITION_MS = 500;
const SCATTER_COLOR_CURSOR_CLEAR_BUFFER_MS = 80;

function setColorReloadCursor(on: boolean): void {
    if (typeof document === "undefined") return;
    document.documentElement.classList.toggle(COLOR_RELOADING_CLASS, on);
}

/** After synchronous chart work (e.g. drawChart), clear wait after the next paint(s). */
function scheduleClearColorReloadCursorAfterPaint(): void {
    queueMicrotask(() => {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                setColorReloadCursor(false);
            });
        });
    });
}

function clearPerformanceSelectionHotkeyBusy(): void {
    if (typeof document === "undefined") return;
    document.documentElement.classList.remove(PERFORMANCE_SELECTION_HOTKEY_BUSY_CLASS);
}

/** Marks each AdvancedScatter root for “how many scatters on page” fallback (SELECTIONS digit hotkey). */
const SCATTER_SELECTIONS_DIGIT_HOTKEY_ROOT_CLASS = "advanced-scatter-selections-digit-hotkey-root";

/**
 * When several AdvancedScatters register window keydown, only the instance whose root last had pointer
 * should show “Computing selection…” and run assign; otherwise the first listener’s DOM shows the banner on the wrong chart.
 */
let scatterSelectionsDigitPointerOwnerRoot: HTMLElement | null = null;

function scatterSelectionsDigitSetPointerOwnerRoot(root: HTMLElement): void {
    scatterSelectionsDigitPointerOwnerRoot = root;
}

function scatterSelectionsDigitClearPointerOwnerRootIfMatch(root: HTMLElement): void {
    if (scatterSelectionsDigitPointerOwnerRoot === root) {
        scatterSelectionsDigitPointerOwnerRoot = null;
    }
}

function scatterSelectionsDigitThisChartMayHandle(
    hotkeyRootEl: HTMLElement | null,
    chartRef: HTMLDivElement | null,
): boolean {
    if (hotkeyRootEl == null || chartRef == null) return false;
    if (scatterSelectionsDigitPointerOwnerRoot === hotkeyRootEl) return true;
    if (scatterSelectionsDigitPointerOwnerRoot != null) return false;
    if (typeof document === "undefined") return false;
    const roots = document.querySelectorAll<HTMLElement>(`.${SCATTER_SELECTIONS_DIGIT_HOTKEY_ROOT_CLASS}`);
    return roots.length === 1 && roots[0] === hotkeyRootEl;
}

/**
 * Loess smoothing span (fraction of points in each local window). Higher = smoother;
 * FitTable shows it as "Smoothness 1..10" (= span * 10).
 */
const LOESS_BANDWIDTH_MIN = 0.1;
const LOESS_BANDWIDTH_MAX = 1;
/** Default is max smoothness — zoomed fits should read as a trend line, not follow every wiggle. */
const LOESS_BANDWIDTH_DEFAULT = LOESS_BANDWIDTH_MAX;

/**
 * Last value the user set with the FitTable +/- buttons. Entering/leaving zoom swaps between two
 * separate AdvancedScatter instances (see the `<Show when={zoom()}>` pairs in the report pages), so a
 * component-local signal alone would reset to the default on every zoom toggle. Seeding from here keeps
 * the user's choice across those remounts.
 */
let lastLoessBandwidth = LOESS_BANDWIDTH_DEFAULT;

/** Clamp to range and snap to one decimal (repeated +/- 0.1 otherwise drifts to 0.6000000000000001). */
function clampLoessBandwidth(value: number): number {
    const clamped = Math.max(LOESS_BANDWIDTH_MIN, Math.min(LOESS_BANDWIDTH_MAX, value));
    return Math.round(clamped * 10) / 10;
}

interface AdvancedScatterProps {
    /**
     * Which selection domain this instance reads and writes. Defaults to `'maneuver'` so every
     * existing host keeps its current behaviour.
     *
     * Performance surfaces pass `'aggregate'`: their points are BIN 10 aggregate ids, which must not
     * share an array with maneuver ids or the two pages erase each other in split view. See
     * `selectedAggregateEvents` in selectionStore.
     */
    selectionDomain?: SelectionDomain;
    // Data props (cloud optional for backward compatibility)
    aggregates: any;
    aggregatesAVG?: any;  // NEW - separate aggregate data by type
    aggregatesSTD?: any;  // NEW
    aggregatesAAV?: any;  // NEW
    cloud?: any;  // Optional - only PerfScatter uses this
    targets?: any;
    /** Selected target name; used to detect target changes and redraw target curves. */
    targetName?: string;
    /** Stroke color for the target curve overlay. */
    targetColor?: string;
    /**
     * Baseline overlays to draw, one per baseline slot. Empty or omitted draws none; a slot with no
     * baseline selected is simply absent from the list.
     */
    baselines?: BaselineOverlay[];

    // Axis props
    xaxis: string;
    yaxis: string;
    taxis: string;
    
    // Filter props
    filters: string[];
    uwDw?: string;
    updateCharts?: boolean;
    
    // Color props (support both modes)
    color?: string;
    groups?: Array<{ name: string; color: string }>;
    colorScale?: (source: string) => string;  // FleetScatter feature
    selectedSource?: string;  // FleetScatter feature
    
    // Aggregate type (backward compatibility - use yType for y-axis)
    aggregate?: string;
    xType?: string;  // NEW - aggregate type for x-axis
    yType?: string;  // NEW - aggregate type for y-axis
    
    // Interaction props
    zoom?: boolean;
    handleZoom?: (info: any[]) => void;
    mouseID?: any;
    setMouseID?: (id: number | null) => void;
    highlights?: any[];
    onDataUpdate?: () => void;
    /** Called when the chart has finished drawing (e.g. after upwind/downwind switch). Used to hide mode-switching overlay. */
    onChartRendered?: () => void;
    /** Info/warning message for bottom-right icon and tooltip */
    infoType?: string;
    infoMessage?: string;
    /** Client-only SELECTIONS bucket (0–9); no API / reader gate. */
    onLocalSelectionAssign?: (eventIds: number[], value: number) => void;
    /** When true, point opacity reflects Datetime age (Live Performance scatter only). */
    liveRecencyOpacity?: boolean;
    [key: string]: any;
}

export default function AdvancedScatter(props: AdvancedScatterProps) {
    // Signals
    const [filtered, setFiltered] = createSignal<any[]>([]);
    const [isUpdatingGrade, setIsUpdatingGrade] = createSignal(false);
    const [fitData, setFitData] = createSignal<any[]>([]);
    const [fitDataVersion, setFitDataVersion] = createSignal(0);
    /**
     * Fit lines: always with Display Options → Fits ON; otherwise only on the expanded chart, and not
     * there either while Highlight Fits & Targets is ON (that mode then tabulates the references).
     */
    const fitsEnabled = (): boolean =>
        performanceDisplayStore.showScatterFits() ||
        (props.zoom === true && !performanceDisplayStore.highlightScatterLines());
    /**
     * Expanded chart, Highlight Fits & Targets ON, Fits OFF: the side table tabulates the target and
     * baseline curves instead of the fits. Any other combination keeps the fit table.
     */
    const referenceTableMode = (): boolean =>
        props.zoom === true &&
        performanceDisplayStore.highlightScatterLines() &&
        !performanceDisplayStore.showScatterFits();
    /** The target and baseline curves as last drawn. */
    const [referenceCurves, setReferenceCurves] = createSignal<{ target: any[]; baselines: ReferenceCurve[] }>({
        target: [],
        baselines: [],
    });
    /** Table groups for referenceTableMode, one column per curve that is drawn, in its own colour. */
    const referenceTableGroups = createMemo(() => {
        const curves = referenceCurves();
        const toFitValues = (rows: any[]): Array<{ x: number; y: number }> =>
            rows
                .map((r) => ({ x: Number(r?.X), y: Number(r?.Y) }))
                .filter((v) => Number.isFinite(v.x) && Number.isFinite(v.y))
                .sort((a, b) => a.x - b.x);
        const groups = [
            { group: "TARGET", color: props.targetColor || '#00ff7f', fitValues: toFitValues(curves.target) },
            ...curves.baselines.map((b) => ({
                group: String(b.name).toUpperCase(),
                color: b.color,
                fitValues: toFitValues(b.points),
            })),
        ];
        return groups.filter((g) => g.fitValues.length >= 2);
    });
    const tableData = () => (referenceTableMode() ? referenceTableGroups() : fitData());
    /** Color By DENSITY / VMG: each drawn point's 0..1 heat for this chart, refreshed per draw. */
    let pointDensity = new WeakMap<object, number>();
    let pointVmg = new WeakMap<object, number>();
    const [loessBandwidth, setLoessBandwidth] = createSignal(lastLoessBandwidth);
    const [isComputingLassoSelection, setIsComputingLassoSelection] = createSignal(false);
    /** In-chart banner for SELECTIONS digit hotkeys (validation / applying); cleared with hotkey wait cursor lifecycle. */
    const [selectionsHotkeyBanner, setSelectionsHotkeyBanner] = createSignal<string | null>(null);
    let selectionsHotkeyBannerClearTimer: ReturnType<typeof setTimeout> | null = null;

    function clearSelectionsHotkeyBannerAndTimer(): void {
        if (selectionsHotkeyBannerClearTimer != null) {
            clearTimeout(selectionsHotkeyBannerClearTimer);
            selectionsHotkeyBannerClearTimer = null;
        }
        setSelectionsHotkeyBanner(null);
    }

    /**
     * After SELECTIONS digit assign with selected points: Performance.tsx defers
     * `setUpdateCharts` / `handleColorChange` in a `requestAnimationFrame`, then charts redraw.
     * Clear wait cursor and banner after that pipeline.
     */
    function scheduleClearPerformanceSelectionHotkeyAfterAssign(): void {
        queueMicrotask(() => {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        clearPerformanceSelectionHotkeyBusy();
                        clearSelectionsHotkeyBannerAndTimer();
                        setIsComputingLassoSelection(false);
                        setLassoComputingCursor(false);
                    });
                });
            });
        });
    }

    const [isVisible, setIsVisible] = createSignal(false);
    /** Window keydown is not a reactive subscriber; sync color so GRADE/STATE hotkeys do not run after switching to SELECTIONS. */
    const [colorModeForHotkeys, setColorModeForHotkeys] = createSignal<string>(props.color ?? '');
    createEffect(() => {
        setColorModeForHotkeys(props.color ?? '');
    });

    // Refs and observers
    let containerRef: HTMLElement | null = document.getElementById('main-content');
    let chartRef: HTMLDivElement | null = null;
    /** Outer flex wrapper (chart + banner + fit table); used to attribute SELECTIONS digit hotkey to the correct chart. */
    let scatterSelectionsDigitHotkeyRootEl: HTMLDivElement | null = null;
    let intersectionObserver: IntersectionObserver | null = null;
    let listenersAttached = false;
    let lastCloudCheckTime = 0;
    const CLOUD_CHECK_THROTTLE_MS = 500; // Throttle cloud checks to prevent excessive redraws
    let lastVisibilityEffectTime = 0;
    const VISIBILITY_EFFECT_THROTTLE_MS = 1000; // Throttle visibility effect cloud checks
    let lastVisibilityState = false;
    let isCleaningUp = false; // Flag to track if component is being cleaned up
    
    // Store listener functions so they can be reused for attach/detach
    let storedMouseover: ((event: MouseEvent, d: any) => void) | null = null;
    let storedMouseout: (() => void) | null = null;
    let storedClick: ((event: MouseEvent, d: any) => void) | null = null;

    // Use defaultChannelsStore for channel names
    const { twaName, bspName, twsName } = defaultChannelsStore;

    // Chart dimensions and scales
    let xMin = 9999999;
    let xMax = -9999999;
    let yMin = 9999999;
    let yMax = -9999999;
    let margin = { top: 10, right: 10, bottom: 80, left: 50 };
    let chartWidth = 450 - margin.left - margin.right;
    let chartHeight = 500 - margin.top - margin.bottom;
    let xScale: d3.ScaleLinear<number, number, never> = d3.scaleLinear();
    let yScale: d3.ScaleLinear<number, number, never> = d3.scaleLinear();

    // Flags and workers
    let allowclick = true;
    let isLassoActive = false;
    let isComputingSelection = false;
    let lassoWorker: Worker | null = null;
    let lastGradeUpdateTime = 0;
    let isUpdatingData = false; // Flag to prevent filter effect during data updates

    // High-density (canvas) scatter mode. When the rendered point count exceeds the
    // threshold we paint all points on a <canvas> layer instead of per-point SVG nodes.
    // SVG still owns axes, grid, target/fit lines and a transparent hit rect for interaction.
    let isHighDensityMode = false;
    let canvasScatterData: any[] = [];
    let canvasLassoInstance: ReturnType<typeof lasso> | null = null;
    const CANVAS_POINTS_CLASS = "advanced-scatter-points-canvas";
    const CANVAS_HIT_AREA_CLASS = "canvas-scatter-hit-area";

    // Captured prop values for use in non-reactive contexts
    let lastCapturedAggregates: any[] | undefined = undefined;
    let lastCapturedCloud: any[] | undefined = undefined;

    // Color functions (will be redefined in drawScatter)
    let getFillColor: ((d: any) => string) | undefined;
    let currentRecencyScale: RecencyOpacityScale | null = null;

    const isLiveRecencyOpacityEnabled = (): boolean => props.liveRecencyOpacity === true;

    /**
     * Domain-scoped selection access. `'maneuver'` (the default) resolves to exactly the calls this
     * component made before the split, so nothing changes for the maneuver hosts.
     */
    const isAggregateDomain = (): boolean => props.selectionDomain === 'aggregate';
    /** The manual selection this chart ACTS on — clicks, lasso, hotkeys, hasSelection. */
    const domainSelectedEvents = (): number[] =>
        isAggregateDomain() ? selectedAggregateEvents() : selectedEvents();
    const setDomainSelectedEvents = (value: number[] | ((prev: number[]) => number[])): void => {
        if (isAggregateDomain()) { setSelectedAggregateEvents(value); return; }
        setSelectedEvents(value as number[]);
    };
    /**
     * What a point should LOOK like: manual selection union the live auto-follow highlight. Only
     * appearance may read this — acting on it would silently fold the live edge into gestures the
     * user never made.
     */
    const domainHighlightedIds = (): number[] =>
        isAggregateDomain() ? highlightedAggregateEventIds() : highlightedEventIds();

    const refreshRecencyScaleFromData = (data: any[]): void => {
        if (!isLiveRecencyOpacityEnabled()) {
            currentRecencyScale = null;
            return;
        }
        currentRecencyScale = buildRecencyOpacityScale(data ?? []);
    };

    const getScatterPointOpacity = (d: any, selectedOverride?: number[]): number => {
        // Appearance: the union, so Live Performance's newest point is emphasised without joining
        // the manual selection. See `domainHighlightedIds`.
        const selectedRaw = selectedOverride ?? domainHighlightedIds() ?? [];
        const selectedIds = selectedRaw
            .map((id) => Number(id))
            .filter((id) => Number.isInteger(id) && id > 0);
        return resolveScatterPointOpacity(d, {
            recencyScale: currentRecencyScale,
            selectedIds,
            liveRecencyEnabled: isLiveRecencyOpacityEnabled(),
        });
    };

    const getScatterPointSizeBonus = (d: any): number =>
        resolveScatterPointRecencySizeBonus(d, {
            recencyScale: currentRecencyScale,
            liveRecencyEnabled: isLiveRecencyOpacityEnabled(),
        });

    /** Point radius / square size given selection state (matches updatePointSizesAndOpacity). */
    function scatterPointGeometry(
        d: any,
        isSelected: boolean,
        hasSelection: boolean,
        isHovered = false
    ): { r: number; rectSize: number } {
        const bonus = getScatterPointSizeBonus(d);
        if (isHovered) return { r: 10 + bonus, rectSize: 14 + bonus };
        if (!hasSelection) return { r: 5 + bonus, rectSize: 8.25 + bonus };
        return isSelected
            ? { r: 6 + bonus, rectSize: 11 + bonus }
            : { r: 4 + bonus, rectSize: 6 + bonus };
    }

    const applyScatterPointOpacity = (
        selection: d3.Selection<any, any, any, any>,
        selectedOverride?: number[]
    ): void => {
        selection.each(function (d: any) {
            if (d?.ID === undefined || d?.ID === null) return;
            d3.select(this).style('opacity', getScatterPointOpacity(d, selectedOverride));
        });
    };

    const applyScatterOpacityToChart = (): void => {
        if (!chartRef) return;
        applyScatterPointOpacity(d3.select(chartRef).selectAll(".scatter"));
    };

    // Map of Twa_deg_avg from AVG aggregates for filtering STD/AAV aggregates
    // This is updated in drawChart and used by getTwa
    let avgTwaDegAvgMap = new Map<string | number, number>();

    // Update captured values whenever props change (within reactive context)
    createEffect(() => {
        if (isCleaningUp) return; // Don't run during cleanup
        lastCapturedAggregates = props.aggregates;
        const previousCloud = lastCapturedCloud;
        const currentCloud = props.cloud;
        lastCapturedCloud = currentCloud;
        
        // Remove cloud area if cloud is empty or not provided
        const cloudIsEmpty = !currentCloud || (Array.isArray(currentCloud) && currentCloud.length === 0);
        const cloudWasRemoved = previousCloud && (Array.isArray(previousCloud) && previousCloud.length > 0) && cloudIsEmpty;
        
        if (cloudIsEmpty || cloudWasRemoved) {
            // Immediately remove cloud area synchronously
            if (chartRef) {
                const svg = d3.select(chartRef).select("svg");
                if (svg.node()) {
                    // Remove from all possible locations synchronously
                    svg.selectAll(".cloud-area").remove();
                    svg.selectAll("g.cloud-area").remove();
                    const chartbody = svg.select("g");
                    if (chartbody.node()) {
                        chartbody.selectAll(".cloud-area").remove();
                        chartbody.selectAll("g.cloud-area").remove();
                    }
                    // Also call removeCloudArea for any other locations
                    removeCloudArea();
                    
                    // If chart is visible and has data elements, immediately redraw to ensure cloud is removed
                    const hasScatterPoints = chartHasRenderedPoints();
                    if (isVisible() && hasScatterPoints) {
                        // Use current props.cloud (empty array) directly, not lastCapturedCloud
                        logDebug('AdvancedScatter: Triggering immediate redraw to remove cloud');
                        drawDataElementsOnly(lastCapturedAggregates, currentCloud);
                    }
                } else {
                    // Chart not rendered yet, but still remove from any existing elements
                    removeCloudArea();
                }
            } else {
                // No chart ref yet, but still try to remove
                removeCloudArea();
            }
        }
    });

    // Helper function to get TWA value
    // Twa_deg_avg is metadata (always from AVG) - use it for consistent upwind/downwind filtering
    // Twa_deg is the actual data field for the aggregate type being queried
    // After fixes, Twa_deg_avg should be present in all aggregates (AVG, STD, AAV)
    const getTwa = (d: any): number => {
        if (!d) return 0;
        
        // First, check Twa_deg_avg directly in the data point (should be present in all aggregates)
        const twaAvg = d.Twa_deg_avg ?? d.twa_deg_avg;
        if (twaAvg !== undefined && twaAvg !== null && !isNaN(Number(twaAvg))) {
            return Number(twaAvg);
        }
        
        // Fallback: If Twa_deg_avg is missing (backward compatibility), try AVG aggregates map
        if (d.event_id !== undefined && avgTwaDegAvgMap.has(d.event_id)) {
            return avgTwaDegAvgMap.get(d.event_id)!;
        }
        
        // Fallback: Try to find in props.aggregatesAVG array
        if (props.aggregatesAVG && Array.isArray(props.aggregatesAVG) && d.event_id !== undefined) {
            const avgPoint = props.aggregatesAVG.find((avg: any) => avg.event_id === d.event_id);
            if (avgPoint) {
                const avgTwaAvg = avgPoint.Twa_deg_avg ?? avgPoint.twa_deg_avg;
                if (avgTwaAvg !== undefined && avgTwaAvg !== null && !isNaN(Number(avgTwaAvg))) {
                    return Number(avgTwaAvg);
                }
            }
        }
        
        // Last resort: fallback to regular Twa_deg field (data field for the aggregate type)
        // This is NOT ideal for filtering but better than returning 0
        const twaField = twaName();
        const val = d[twaField] ?? d.Twa_deg ?? 0;
        return Number(val) || 0;
    };

    // Unified color function supporting both source-based and groups-based coloring
    const getColor = (d: any): string => {
        // DENSITY has no groups: the heat was computed per point at draw time (refreshPointDensity).
        if (props.color === PERFORMANCE_DENSITY_COLOR) {
            return densityHeatColor(pointDensity.get(d as object) ?? 0);
        }
        // VMG likewise: placed on this chart's VMG range at draw time (refreshPointVmg).
        if (props.color === PERFORMANCE_VMG_COLOR) {
            const fraction = pointVmg.get(d as object);
            return fraction === undefined ? 'lightgrey' : vmgHeatColor(fraction);
        }
        // If colorScale is provided AND color is SOURCE_NAME, use colorScale
        if (props.color === 'SOURCE_NAME' && props.colorScale && typeof props.colorScale === 'function') {
            const sourceName = d.source_name || d.sourceName || d.source || d.SOURCE || d.SOURCE_NAME;
            if (sourceName) {
                const normalizedSource = String(sourceName).toLowerCase();
                try {
                    const color = props.colorScale(normalizedSource);
                    // Ensure we got a valid color string
                    if (color && typeof color === 'string' && color !== 'undefined' && color !== 'null') {
                        return color;
                    }
                } catch (error) {
                    logDebug('AdvancedScatter: Error calling colorScale', { error, sourceName: normalizedSource });
                }
            }
        }

        // If color prop is provided and groups are available, use them
        if (props.color && props.groups && Array.isArray(props.groups)) {
            // For STATE, data is normalized to lowercase 'state' field
            let value: any;
            if (props.color === 'STATE') {
                // Try multiple field name variations for STATE
                value = d.state || d.State || d.STATE || resolveDataField(d, 'state') || resolveDataField(d, 'STATE');
            } else {
                value = resolveDataField(d, props.color);
            }

            // Special handling for race number: convert -1 to 'TRAINING'
            if (isRaceColorMode(props.color) && (value === -1 || value === '-1')) {
                value = 'TRAINING';
            }

            // Collapsed race legend: one bucket for all non-training races
            if (isRaceColorMode(props.color) && props.groups) {
                const hasRacingBucket = props.groups.some(
                    (g) => String(g.name) === RACE_NUMBER_COLLAPSED_LEGEND_NAME
                );
                if (hasRacingBucket) {
                    if (isTrainingRaceMetadataValue(value)) {
                        const tg = props.groups.find((g) => String(g.name) === 'TRAINING');
                        return tg ? tg.color : 'lightgrey';
                    }
                    const rg = props.groups.find((g) => String(g.name) === RACE_NUMBER_COLLAPSED_LEGEND_NAME);
                    return rg ? rg.color : 'lightgrey';
                }
            }

            // When colored by LEG, training / -1 always use light grey (match legend)
            if (props.color === 'LEG') {
                const note = value !== undefined && value !== null ? String(value) : '';
                if (note === 'TRAINING' || note === 'training' || note === '-1' || value === -1) {
                    return 'lightgrey';
                }
            }

            // Missing and NONE use the same grey as the legend swatch, not D3's first category blue.
            if (isEmptyGroupValue(value)) {
                return EMPTY_GROUP_COLOR;
            }
            const valueStr = value !== undefined && value !== null ? String(value) : null;
            let group = null;
            if (valueStr !== null) {
                group = props.groups.find(group => {
                    // For STATE, use case-insensitive comparison
                    if (props.color === 'STATE') {
                        return String(group.name).toLowerCase() === valueStr.toLowerCase();
                    }
                    // Exact match
                    if (String(group.name) === valueStr) return true;
                    // Numeric comparison
                    const groupNum = Number(group.name);
                    const valueNum = Number(valueStr);
                    if (!isNaN(groupNum) && !isNaN(valueNum) && groupNum === valueNum) return true;
                    // Case-insensitive match
                    if (String(group.name).toLowerCase() === valueStr.toLowerCase()) return true;
                    return false;
                });
            }

            return group ? group.color : "lightgrey";
        }

        return "lightgrey";
    };

    // Filter data by position and tack
    function filterData(collection: any[], pos: string, tack: string): any[] {
        try {
            if (collection != undefined) {
                collection.forEach(function (d: any) {
                    const twa = getTwa(d);
                    if (twa < 0) {
                        d.tack = 'PORT';
                    } else {
                        d.tack = 'STBD';
                    }
                    d.Datetime = formatDateTime(d.Datetime);
                });

                if (pos === 'UW') {
                    if (tack === 'PORT') {
                        return collection.filter((d: any) => {
                            const twa = getTwa(d);
                            return twa < 0 && Math.abs(twa) < 90;
                        });
                    } else if (tack === 'STBD') {
                        return collection.filter((d: any) => {
                            const twa = getTwa(d);
                            return twa > 0 && Math.abs(twa) < 90;
                        });
                    } else if (tack === 'BOTH') {
                        return collection.filter((d: any) => {
                            const twa = getTwa(d);
                            return Math.abs(twa) < 90;
                        });
                    }
                    return [];
                } else if (pos === 'DW') {
                    if (tack === 'PORT') {
                        return collection.filter((d: any) => {
                            const twa = getTwa(d);
                            return twa < 0 && Math.abs(twa) > 90;
                        });
                    } else if (tack === 'STBD') {
                        return collection.filter((d: any) => {
                            const twa = getTwa(d);
                            return twa > 0 && Math.abs(twa) > 90;
                        });
                    } else if (tack === 'BOTH') {
                        return collection.filter((d: any) => {
                            const twa = getTwa(d);
                            return Math.abs(twa) > 90;
                        });
                    }
                    return [];
                } else {
                    return collection;
                }
            } else {
                return [];
            }
        } catch {
            return [];
        }
    }

    /** Every merged scatter point carries a finite numeric `selection` (SELECTIONS / tooltips), regardless of color mode. */
    function normalizeSelectionOnScatterPoints(points: any[]): any[] {
        for (const p of points) {
            if (p == null || typeof p !== 'object') continue;
            const raw = resolveDataField(p, 'SELECTIONS') ?? p.selection;
            const n = Number(raw);
            p.selection = Number.isFinite(n) ? n : 0;
        }
        return points;
    }

    // Reduce data to X/Y coordinates with metadata
    // Now supports separate xType and yType for x-axis and y-axis aggregate types
    function reduceData(collection: any[], x: string, y: string, xDataCollection?: any[], yDataCollection?: any[]): any[] {
        // If separate x and y data collections are provided, use them; otherwise use the main collection
        // The passed collections are already filtered and contain the correct aggregate type data
        let xCollectionToUse = xDataCollection || collection;
        let yCollectionToUse = yDataCollection || collection;
        
        // If collections weren't provided but we have separate aggregate data, use the appropriate type
        if (!xDataCollection && !yDataCollection && (props.aggregatesAVG || props.aggregatesSTD || props.aggregatesAAV)) {
            const xType = (props.xType || props.aggregate || 'AVG').toUpperCase();
            const yType = (props.yType || props.aggregate || 'AVG').toUpperCase();
            const aggregatesByType: { [key: string]: any[] } = {
                AVG: props.aggregatesAVG || [],
                STD: props.aggregatesSTD || [],
                AAV: props.aggregatesAAV || []
            };
            xCollectionToUse = aggregatesByType[xType] || collection;
            yCollectionToUse = aggregatesByType[yType] || collection;
        }
        // Filter out hidden events so they are not rendered
        xCollectionToUse = (xCollectionToUse || []).filter((d: any) => d.event_id == null || d.event_id === undefined || !isEventHidden(Number(d.event_id)));
        yCollectionToUse = (yCollectionToUse || []).filter((d: any) => d.event_id == null || d.event_id === undefined || !isEventHidden(Number(d.event_id)));
        
        // Create a map of AVG data by event_id for TACK lookup (TACK from API should be correct for all aggregate types)
        // But prefer TACK from AVG data point when combining x/y from different aggregate types
        // Also create a map for Twa_deg_avg lookup for filtering STD/AAV aggregates
        const avgDataMap = new Map<string | number, any>();
        const avgTwaDegAvgMap = new Map<string | number, number>();
        if (props.aggregatesAVG && Array.isArray(props.aggregatesAVG)) {
            props.aggregatesAVG.forEach((d: any) => {
                const eventId = d.event_id;
                if (eventId !== undefined && eventId !== null) {
                    avgDataMap.set(eventId, d);
                    // Store Twa_deg_avg for quick lookup during filtering
                    const twaDegAvg = d.Twa_deg_avg ?? d.twa_deg_avg;
                    if (twaDegAvg !== undefined && twaDegAvg !== null && !isNaN(Number(twaDegAvg))) {
                        avgTwaDegAvgMap.set(eventId, Number(twaDegAvg));
                    }
                }
            });
        }
        
        if ((xCollectionToUse != undefined && xCollectionToUse.length > 0) || (yCollectionToUse != undefined && yCollectionToUse.length > 0)) {
            
            // Detect if this is cloud data (1Hz) vs aggregate data
            // Cloud data has many points per event_id, aggregate data has one point per event_id
            // Check if we have multiple points with the same event_id
            const eventIdCounts = new Map<string | number, number>();
            const collectionToCheck = xCollectionToUse.length > 0 ? xCollectionToUse : yCollectionToUse;
            collectionToCheck.forEach((d: any) => {
                const eventId = d.event_id;
                if (eventId !== undefined && eventId !== null) {
                    eventIdCounts.set(eventId, (eventIdCounts.get(eventId) || 0) + 1);
                }
            });
            const maxPointsPerEvent = Math.max(...Array.from(eventIdCounts.values()), 0);
            const isCloudData = maxPointsPerEvent > 1; // Cloud data has multiple points per event_id
            
            if (isCloudData) {
                // For cloud data (1Hz), process each point individually - don't group by event_id
                // Both x and y values should be in the same point (same collection)
                const combinedData = collectionToCheck.map((point: any) => {
                    // Extract x and y values from the same point
                    const xFieldValue = resolveDataField(point, x);
                    const yFieldValue = resolveDataField(point, y);
                    
                    const xFieldExists = xFieldValue !== undefined && xFieldValue !== null;
                    const yFieldExists = yFieldValue !== undefined && yFieldValue !== null;
                    
                    if (xFieldExists && yFieldExists) {
                        const datetime = new Date(point.Datetime || point.datetime);
                        const sourceNameOriginal = point.source_name || point.sourceName || point.source || point.SOURCE || point.SOURCE_NAME || 'Unknown';
                        const sourceKeyLower = String(sourceNameOriginal).toLowerCase();
                        const year = datetime ? datetime.getFullYear() : null;
                        let eventIdNum: number | undefined = undefined;
                        const eventId = point.event_id;
                        if (eventId !== undefined && eventId !== null) {
                            const parsed = typeof eventId === 'string' ? parseInt(eventId, 10) : Number(eventId);
                            eventIdNum = isNaN(parsed) ? undefined : parsed;
                        }
                        
                        const xNum = (xFieldValue !== undefined && xFieldValue !== null && !isNaN(Number(xFieldValue))) ? Number(xFieldValue) : 0;
                        const yNum = (yFieldValue !== undefined && yFieldValue !== null && !isNaN(Number(yFieldValue))) ? Number(yFieldValue) : 0;

                        const selRaw = resolveDataField(point, 'SELECTIONS') ?? point.selection;
                        const selNum = Number(selRaw);
                        const selection = Number.isFinite(selNum) ? selNum : 0;

                        // Single nomenclature: read via resolveDataField; combined point uses lowercase keys only
                        const tack = resolveDataField(point, 'TACK') ?? point.tack ?? 'STBD';
                        const twaDegAvg = point.Twa_deg_avg ?? point.twa_deg_avg ?? undefined;
                        let raceVal = resolveDataField(point, 'RACE');
                        if (raceVal === -1 || raceVal === '-1') raceVal = 'TRAINING';

                        return {
                            ID: eventIdNum,
                            event_id: eventIdNum,
                            Datetime: datetime,
                            datetime,
                            dateStringLocal: point.dateStringLocal,
                            timeStringLocal: point.timeStringLocal,
                            Twa_deg_avg: twaDegAvg,
                            twa_deg_avg: twaDegAvg,
                            tack,
                            race_number: raceVal,
                            leg_number: resolveDataField(point, 'LEG') ?? point.leg_number,
                            grade: resolveDataField(point, 'GRADE') ?? point.grade,
                            selection,
                            state: resolveDataField(point, 'STATE') ?? point.state,
                            config: resolveDataField(point, 'CONFIG') ?? point.config,
                            crew: resolveDataField(point, 'CREW') ?? point.crew ?? point.Crew,
                            tests: resolveDataField(point, 'TESTS') ?? point.tests ?? point.Tests,
                            event: resolveDataField(point, 'EVENT') ?? point.event,
                            year: year ?? resolveDataField(point, 'YEAR') ?? point.year,
                            source_name: sourceKeyLower,
                            vmg_perc: vmgPercFromRow(point),
                            X: xNum,
                            Y: yNum
                        };
                    }
                    return null;
                }).filter((item: any) => item !== null);
                
                return normalizeSelectionOnScatterPoints(combinedData);
            } else {
                // For aggregate data, group by event_id (one point per event_id)
                // Create maps of x and y data by event_id for efficient lookup
                const xDataMap = new Map<string | number, any>();
                xCollectionToUse.forEach((d: any) => {
                    const eventId = d.event_id;
                    if (eventId !== undefined && eventId !== null) {
                        // Check if x value exists for this point
                        const xVal = resolveDataField(d, x);
                        if (xVal !== null && xVal !== undefined) {
                            xDataMap.set(eventId, d);
                        }
                    }
                });
                
                const yDataMap = new Map<string | number, any>();
                yCollectionToUse.forEach((d: any) => {
                    const eventId = d.event_id;
                    if (eventId !== undefined && eventId !== null) {
                        // Check if y value exists for this point
                        const yVal = resolveDataField(d, y);
                        if (yVal !== null && yVal !== undefined) {
                            yDataMap.set(eventId, d);
                        }
                    }
                });
                
                // Get all unique event_ids that have both x and y values
                const validEventIds = new Set<string | number>();
                xDataMap.forEach((_, eventId) => {
                    if (yDataMap.has(eventId)) {
                        validEventIds.add(eventId);
                    }
                });
                
                // Create combined data points for each event_id that has both x and y values
                const combinedData = Array.from(validEventIds).map((eventId) => {
                    const xPoint = xDataMap.get(eventId)!;
                    const yPoint = yDataMap.get(eventId)!;
                    const avgPoint = avgDataMap.get(eventId);
                    
                    // Extract x value from x-point using x channel name
                    const xFieldValue = resolveDataField(xPoint, x);
                    // Extract y value from y-point using y channel name
                    const yFieldValue = resolveDataField(yPoint, y);
                    
                    const xFieldExists = xFieldValue !== undefined && xFieldValue !== null;
                    const yFieldExists = yFieldValue !== undefined && yFieldValue !== null;
                    
                    if (xFieldExists && yFieldExists) {
                        const datetime = new Date(xPoint.Datetime || yPoint.Datetime);
                        const sourceNameOriginal = xPoint.source_name || xPoint.sourceName || xPoint.source || xPoint.SOURCE || xPoint.SOURCE_NAME || yPoint.source_name || yPoint.sourceName || 'Unknown';
                        const sourceKeyLower = String(sourceNameOriginal).toLowerCase();
                        const year = datetime ? datetime.getFullYear() : null;
                        let eventIdNum: number | undefined = undefined;
                        if (eventId !== undefined && eventId !== null) {
                            const parsed = typeof eventId === 'string' ? parseInt(eventId, 10) : Number(eventId);
                            eventIdNum = isNaN(parsed) ? undefined : parsed;
                        }
                        
                        const xNum = (xFieldValue !== undefined && xFieldValue !== null && !isNaN(Number(xFieldValue))) ? Number(xFieldValue) : 0;
                        const yNum = (yFieldValue !== undefined && yFieldValue !== null && !isNaN(Number(yFieldValue))) ? Number(yFieldValue) : 0;

                        const selResolved =
                            resolveDataField(xPoint, 'SELECTIONS') ??
                            resolveDataField(yPoint, 'SELECTIONS') ??
                            resolveDataField(avgPoint, 'SELECTIONS');
                        const selNumAgg = Number(selResolved ?? xPoint.selection ?? yPoint.selection);
                        const selection = Number.isFinite(selNumAgg) ? selNumAgg : 0;

                        // Single nomenclature: read via resolveDataField; combined point uses lowercase keys only
                        const tack = resolveDataField(avgPoint ?? yPoint, 'TACK') ?? resolveDataField(yPoint, 'TACK') ?? resolveDataField(xPoint, 'TACK') ?? yPoint.tack ?? xPoint.tack ?? 'STBD';
                        const twaDegAvg = avgPoint?.Twa_deg_avg ?? avgPoint?.twa_deg_avg ?? yPoint?.Twa_deg_avg ?? yPoint?.twa_deg_avg ?? xPoint?.Twa_deg_avg ?? xPoint?.twa_deg_avg ?? undefined;
                        let raceVal = resolveDataField(xPoint, 'RACE') ?? resolveDataField(yPoint, 'RACE');
                        if (raceVal === -1 || raceVal === '-1') raceVal = 'TRAINING';

                        return {
                            ID: eventIdNum,
                            Datetime: datetime,
                            datetime,
                            dateStringLocal: xPoint.dateStringLocal ?? yPoint.dateStringLocal,
                            timeStringLocal: xPoint.timeStringLocal ?? yPoint.timeStringLocal,
                            Twa_deg_avg: twaDegAvg,
                            twa_deg_avg: twaDegAvg,
                            tack,
                            race_number: raceVal,
                            leg_number: resolveDataField(xPoint, 'LEG') ?? resolveDataField(yPoint, 'LEG') ?? xPoint.leg_number ?? yPoint.leg_number,
                            grade: resolveDataField(xPoint, 'GRADE') ?? resolveDataField(yPoint, 'GRADE') ?? xPoint.grade ?? yPoint.grade,
                            selection,
                            state: resolveDataField(xPoint, 'STATE') ?? resolveDataField(yPoint, 'STATE') ?? xPoint.state ?? yPoint.state,
                            config: resolveDataField(xPoint, 'CONFIG') ?? resolveDataField(yPoint, 'CONFIG') ?? xPoint.config ?? yPoint.config,
                            crew:
                              resolveDataField(xPoint, 'CREW') ??
                              resolveDataField(yPoint, 'CREW') ??
                              xPoint.crew ??
                              yPoint.crew ??
                              xPoint.Crew ??
                              yPoint.Crew,
                            tests:
                              resolveDataField(xPoint, 'TESTS') ??
                              resolveDataField(yPoint, 'TESTS') ??
                              resolveDataField(avgPoint, 'TESTS') ??
                              xPoint.tests ??
                              yPoint.tests ??
                              xPoint.Tests ??
                              yPoint.Tests,
                            event: resolveDataField(xPoint, 'EVENT') ?? resolveDataField(yPoint, 'EVENT') ?? xPoint.event ?? yPoint.event,
                            year: year ?? resolveDataField(xPoint, 'YEAR') ?? resolveDataField(yPoint, 'YEAR') ?? xPoint.year ?? yPoint.year,
                            source_name: sourceKeyLower,
                            vmg_perc: vmgPercFromRow(avgPoint) ?? vmgPercFromRow(xPoint) ?? vmgPercFromRow(yPoint),
                            X: xNum,
                            Y: yNum
                        };
                    }
                    return null;
                }).filter((item: any) => item !== null);
                
                return normalizeSelectionOnScatterPoints(combinedData);
            }
        } else {
            return [];
        }
    }

    // Reduce targets to X/Y coordinates
    function reduceTargets(collection: any[], x: string, y: string): any[] {
        logDebug('AdvancedScatter reduceTargets: Called', {
            collectionLength: collection?.length || 0,
            x,
            y,
            firstItemKeys: collection?.[0] ? Object.keys(collection[0]) : []
        });
        
        if (collection != undefined && collection.length > 0) {
            // Try both original case and lowercase - data fields may be in either format
            const xFieldLower = x.toLowerCase();
            const yFieldLower = y.toLowerCase();
            
            // For target data, fields are typically lowercase without suffix (e.g., "tws", "bsp", "twa")
            // But channel names from defaultChannelsStore may have suffix (e.g., "Tws_kph", "Bsp_kph", "Twa_deg")
            // Also handle "_target" suffix which can be added when yaxisTarget is not explicitly set
            // Extract base field name by removing common suffixes including "_target"
            // Handle patterns like "Bsp_kph_target" -> "bsp", "tws_kph" -> "tws"
            // First remove _target suffix, then remove unit suffixes, then remove remaining underscores
            let xBaseField = xFieldLower;
            // Remove _target suffix first (e.g., "bsp_kph_target" -> "bsp_kph")
            xBaseField = xBaseField.replace(/_target$/i, '');
            // Remove unit suffixes (e.g., "bsp_kph" -> "bsp")
            xBaseField = xBaseField.replace(/[_\s]*(kph|kts|deg|perc)$/i, '');
            // Remove any remaining underscores (e.g., "lwy_n" -> "lwy")
            xBaseField = xBaseField.replace(/[_\s]/g, '');
            
            let yBaseField = yFieldLower;
            // Remove _target suffix first (e.g., "bsp_kph_target" -> "bsp_kph")
            yBaseField = yBaseField.replace(/_target$/i, '');
            // Remove unit suffixes (e.g., "bsp_kph" -> "bsp")
            yBaseField = yBaseField.replace(/[_\s]*(kph|kts|deg|perc)$/i, '');
            // Remove any remaining underscores (e.g., "lwy_n" -> "lwy")
            yBaseField = yBaseField.replace(/[_\s]/g, '');
            
            logDebug('AdvancedScatter reduceTargets: Field matching', {
                x,
                xFieldLower,
                xBaseField,
                y,
                yFieldLower,
                yBaseField,
                firstItem: collection[0],
                availableFields: Object.keys(collection[0])
            });
            
            // Filter data where y field is not null (try multiple field name variations)
            let data = collection.filter((d: any) => {
                return (d[y] !== null && d[y] !== undefined) || 
                       (d[yFieldLower] !== null && d[yFieldLower] !== undefined) ||
                       (d[yBaseField] !== null && d[yBaseField] !== undefined);
            });

            logDebug('AdvancedScatter reduceTargets: After filtering', {
                originalLength: collection.length,
                filteredLength: data.length
            });

            if (data.length > 0) {
                const firstItem = data[0];
                const availableFields = Object.keys(firstItem);
                
                // Try to find x field: try exact match, then lowercase, then base field
                let xField = x;
                if (x in firstItem) {
                    xField = x;
                } else if (xFieldLower in firstItem) {
                    xField = xFieldLower;
                } else if (xBaseField in firstItem) {
                    xField = xBaseField;
                } else {
                    // Try to find by partial match (e.g., "tws" in "Tws_kph" or vice versa)
                    // Also handle "_target" suffix in field matching
                    const xMatch = availableFields.find(f => {
                        const fLower = f.toLowerCase();
                        // Use same extraction logic as xBaseField
                        let fBase = fLower.replace(/_target$/i, '').replace(/[_\s]*(kph|kts|deg|perc)$/i, '').replace(/[_\s]/g, '');
                        return fLower === xBaseField || fBase === xBaseField || fLower === x || fLower === xFieldLower;
                    });
                    if (xMatch) {
                        xField = xMatch;
                        logDebug('AdvancedScatter reduceTargets: Found x field by partial match', { xField, searched: xBaseField });
                    }
                }
                
                // Try to find y field: try exact match, then lowercase, then base field
                let yField = y;
                if (y in firstItem) {
                    yField = y;
                } else if (yFieldLower in firstItem) {
                    yField = yFieldLower;
                } else if (yBaseField in firstItem) {
                    yField = yBaseField;
                } else {
                    // Try to find by partial match
                    // Also handle "_target" suffix in field matching
                    const yMatch = availableFields.find(f => {
                        const fLower = f.toLowerCase();
                        // Use same extraction logic as yBaseField
                        let fBase = fLower.replace(/_target$/i, '').replace(/[_\s]*(kph|kts|deg|perc)$/i, '').replace(/[_\s]/g, '');
                        return fLower === yBaseField || fBase === yBaseField || fLower === y || fLower === yFieldLower;
                    });
                    if (yMatch) {
                        yField = yMatch;
                        logDebug('AdvancedScatter reduceTargets: Found y field by partial match', { yField, searched: yBaseField });
                    }
                }
                
                // Check if both x and y fields exist in the data
                logDebug('AdvancedScatter reduceTargets: Field resolution', {
                    xField,
                    yField,
                    xFieldExists: xField in firstItem,
                    yFieldExists: yField in firstItem,
                    availableFields
                });
                
                if (xField in firstItem && yField in firstItem) {
                    data.forEach((d: any) => {
                        if (d[yField] == undefined || d[yField] == null) {
                            d[yField] = 0;
                        }
                        if (d[xField] == undefined || d[xField] == null) {
                            d[xField] = 0;
                        }
                    });

                    const result = data.map((d: any) => ({
                        X: +d[xField] || 0,
                        Y: +d[yField] || 0
                    }));
                    
                    logDebug('AdvancedScatter reduceTargets: Success', {
                        resultLength: result.length,
                        firstFewPoints: result.slice(0, 3)
                    });
                    
                    return result;
                } else {
                    logError(`AdvancedScatter reduceTargets: Fields not found in data. Looking for x: "${x}" (tried: ${x}, ${xFieldLower}, ${xBaseField}), y: "${y}" (tried: ${y}, ${yFieldLower}, ${yBaseField}). Found xField: "${xField}", yField: "${yField}". Available fields:`, availableFields);
                    logError('AdvancedScatter reduceTargets: First item sample', {
                        firstItem,
                        xFieldValue: firstItem[xField],
                        yFieldValue: firstItem[yField]
                    });
                    return [];
                }
            } else {
                return [];
            }
        } else {
            return [];
        }
    }

    // Get X bounds with minimum range enforcement (from PerfScatter)
    function getXBounds(targets: any[][], cloudData?: any[], aggregateData?: any[]): void {
        xMin = 9999999;
        xMax = -9999999;

        // Check if x-axis is the default (TWA)
        const twaChannel = twaName().toLowerCase();
        const xaxisLower = props.xaxis ? props.xaxis.toLowerCase() : '';
        const isDefaultXAxis = xaxisLower === twaChannel || xaxisLower === 'twa';

        // For default x-axis, use cloud and aggregate points combined
        if (isDefaultXAxis && (cloudData || aggregateData)) {
            // Combine cloud and aggregate data for bounds calculation
            const combinedData: any[] = [];
            if (cloudData && cloudData.length > 0) {
                combinedData.push(...cloudData);
            }
            if (aggregateData && aggregateData.length > 0) {
                combinedData.push(...aggregateData);
            }

            // Calculate min/max from combined cloud and aggregate points
            combinedData.forEach((d: any) => {
                if (d && d.X !== undefined && d.X !== null) {
                    if (d.X > xMax) {
                        xMax = d.X;
                    }
                    if (d.X < xMin) {
                        xMin = d.X;
                    }
                }
            });
        } else {
            // For non-default x-axis or when no cloud/aggregate data, use targets
            targets.forEach((targetArray: any[]) => {
                if (targetArray && targetArray.length > 0) {
                    targetArray.forEach((d: any) => {
                        if (d.X > xMax) {
                            xMax = d.X;
                        }
                        if (d.X < xMin) {
                            xMin = d.X;
                        }
                    });
                }
            });
        }

        // If no valid bounds were found, keep sentinel values
        if (xMin === 9999999 || xMax === -9999999) {
            return;
        }

        // Store original minimum to check if data goes below zero
        const originalXMin = xMin;

        // If x-axis is not the default, set xMax to 10% bigger than the maximum value
        if (!isDefaultXAxis) {
            const maxValue = xMax;
            xMax = maxValue * 1.1; // 10% bigger
        } else {
            // Default x-axis: calculate interval from min/max difference, divide by 10
            // Add one interval to max, subtract one interval from min
            const dataRange = Math.abs(xMax - xMin);
            const interval = dataRange / 10;
            xMax += interval;
            xMin -= interval;
        }

        // Enforce minimum 4-knot range for x-axis to ensure target line visibility (from PerfScatter)
        const minRange = 4;
        const currentRange = xMax - xMin;
        if (currentRange < minRange) {
            const center = (xMax + xMin) / 2;
            xMin = center - minRange / 2;
            xMax = center + minRange / 2;
            // Recalculate interval after minRange adjustment for default x-axis
            if (isDefaultXAxis) {
                const adjustedInterval = Math.abs(xMax - xMin) / 10;
                xMax += adjustedInterval;
                xMin -= adjustedInterval;
            }
        }

        // For default x-axis, ensure we have proper padding by rounding down min and rounding up max
        if (isDefaultXAxis) {
            // Round min down (floor) and max up (ceil) to ensure data fits within bounds
            // This ensures the interval padding is preserved
            xMin = Math.floor(xMin);
            xMax = Math.ceil(xMax);
        } else {
            // For non-default, use original rounding
            xMax = Number((xMax + 0.5).toFixed(0));
            xMin = Number((xMin - 0.5).toFixed(0));
        }
        
        // Set minimum to zero if original data values don't go below zero
        if (originalXMin >= 0 && xMin < 0) {
            xMin = 0;
        }
    }

    // Get Y bounds with STD/AAV handling
    function getYBounds(targets: any[][]): void {
        yMin = 9999999;
        yMax = -9999999;

        targets.forEach((targetArray: any[]) => {
            if (targetArray && targetArray.length > 0) {
                targetArray.forEach((d: any) => {
                    if (d.Y > yMax) {
                        yMax = d.Y;
                    }
                    if (d.Y < yMin) {
                        yMin = d.Y;
                    }
                });
            }
        });

        if (yMin === 9999999 || yMax === -9999999) {
            yMin = 0;
            yMax = 10;
            return;
        }

        // Store original minimum to check if data goes below zero
        const originalYMin = yMin;

        const yType = (props.yType || props.aggregate || 'AVG').toUpperCase();
        if (yType === 'STD' || yType === 'AAV') {
            const actualMax = yMax;
            yMin = 0;
            yMax = Number((actualMax * 1.5).toFixed(1));
        } else {
            let range = yMax - yMin;
            if (Math.abs(range) < 1e-9) {
                const center = yMax;
                const pad = Math.max(0.1, Math.abs(center) * 0.1);
                yMax = Number((center + pad).toFixed(2));
                yMin = Number((center - pad).toFixed(2));
            } else {
                const pad = range * 0.1;
                yMax = Number((yMax + pad).toFixed(2));
                yMin = Number((yMin - pad).toFixed(2));
            }
            
            // Set minimum to zero if original data values don't go below zero
            if (originalYMin >= 0 && yMin < 0) {
                yMin = 0;
            }
        }
    }

    // Draw axes with clipping path support
    function drawAxes() {
        const containerWidth = chartRef?.clientWidth || 450;
        const containerHeight = chartRef?.clientHeight || 500;
        chartWidth = containerWidth - margin.left - margin.right;
        chartHeight = containerHeight - margin.top - margin.bottom;

        const bspChannel = bspName().toLowerCase();
        const twsChannel = twsName().toLowerCase();
        const twaChannel = twaName().toLowerCase();
        const xaxisLower = props.xaxis ? props.xaxis.toLowerCase() : '';
        const isDefaultXAxis = xaxisLower === twaChannel || xaxisLower === 'twa';
        const xType = (props.xType || props.aggregate || 'AVG').toUpperCase();
        
        // Determine unit suffix based on channel name (for backward compatibility with default axes)
        const getUnitSuffix = (channelName: string): string => {
          const lower = channelName.toLowerCase();
          if (lower.includes('kph')) return ' [KPH]';
          if (lower.includes('kts')) return ' [KTS]';
          // Default based on class (GP50 uses kph, others use kts)
          const className = persistantStore.selectedClassName()?.toLowerCase() || '';
          return className === 'gp50' ? ' [KPH]' : ' [KTS]';
        };

        // Strip _kph/_KPH from channel name for x-axis label so we don't duplicate it (unit is shown via suffix)
        const stripKphFromLabel = (channelName: string): string =>
          channelName.replace(/_kph$/i, '').trim();

        // Use actual x-axis channel name with aggregate type, or fall back to default logic
        const xaxisLabel = props.xaxis && xaxisLower !== bspChannel && xaxisLower !== twsChannel
            ? `${stripKphFromLabel(props.xaxis).toUpperCase()} [${xType}]`
            : (xaxisLower === bspChannel || xaxisLower === 'bsp'
                ? `${stripKphFromLabel(bspChannel).toUpperCase()}${getUnitSuffix(bspChannel)}`
                : `${stripKphFromLabel(twsChannel).toUpperCase()}${getUnitSuffix(twsChannel)}`);

        d3.select(chartRef).selectAll("svg").remove();

        let svg = d3.select(chartRef)
            .append("svg")
            .attr("width", chartWidth + margin.left + margin.right)
            .attr("height", chartHeight + margin.top + margin.bottom)
            .on("dblclick", () => {
                const xType = (props.xType || props.aggregate || 'AVG').toUpperCase();
                const yType = (props.yType || props.aggregate || 'AVG').toUpperCase();
                let info = [props.xaxis, props.yaxis, props.taxis, props.filters, lastCapturedCloud, lastCapturedAggregates, props.aggregate || 'AVG', props.infoType, props.infoMessage, xType, yType];
                if (props.zoom) { info = []; }
                if (props.handleZoom) {
                    props.handleZoom(info);
                }
            })
            .on("click", (event) => {
                const target = event.target as HTMLElement | null;
                if (target && (target.tagName === 'svg' ||
                    target.classList.contains('grid') ||
                    target.classList.contains('axes') ||
                    target.tagName === 'g')) {
                    return;
                }
            })
            .on("mouseleave", () => {
                setTooltip({
                    visible: false,
                    content: "",
                    x: 0,
                    y: 0,
                });
            });

        let chart = svg.append("g")
            .attr("transform", "translate(" + margin.left + "," + margin.top + ")");

        // Use calculated bounds for default x-axis (which include interval padding)
        let xDomainMin = xMin;
        let xDomainMax = xMax;
        if (xMin === 9999999 || xMax === -9999999 || xMin > xMax) {
            xDomainMin = 5;
            xDomainMax = 25;
        }
        
        // For default x-axis, ensure we're using the calculated bounds (which include interval padding)
        if (isDefaultXAxis && xMin !== 9999999 && xMax !== -9999999) {
            xDomainMin = xMin;
            xDomainMax = xMax;
        }
        
        xScale = d3.scaleLinear().range([0, chartWidth]).domain([xDomainMin, xDomainMax]);
        chart.append("g")
            .attr("class", "x-axis")
            .attr("transform", "translate(0," + chartHeight + ")")
            .call(d3.axisBottom(xScale));

        yScale = d3.scaleLinear().range([chartHeight, 0]).domain([yMin, yMax]);
        chart.append("g")
            .attr("class", "y-axis")
            .call(d3.axisLeft(yScale));

        svg.selectAll(".x-label, .y-label").remove();

        const yType = (props.yType || props.aggregate || 'AVG').toUpperCase();
        const yAxisLabel = `${props.yaxis.toUpperCase()} [${yType}]`;

        svg.append("text")
            .attr("class", "y-label chart-element")
            .style("text-anchor", "start")
            .attr("x", margin.left + 25)
            .attr("y", margin.top + 10)
            .attr("font-size", "16px")
            .text(yAxisLabel);

        svg.append("text")
            .attr("class", "x-label chart-element")
            .attr("text-anchor", "middle")
            .attr("transform", `translate(${margin.left + chartWidth / 2},${margin.top + chartHeight + 40})`)
            .attr("font-size", "16px")
            .text(xaxisLabel);

        chart.append("g")
            .attr("class", "grid")
            .attr("transform", "translate(0," + chartHeight + ")")
            .style("stroke-dasharray", ("3,3"))
            .call(d3.axisBottom(xScale)
                .ticks(5)
                .tickSize(-chartHeight)
                .tickFormat(() => "")
            );

        chart.append("g")
            .attr("class", "grid")
            .style("stroke-dasharray", ("3,3"))
            .call(d3.axisLeft(yScale)
                .ticks(5)
                .tickSize(-chartWidth)
                .tickFormat(() => "")
            );

        // Create clipping path for chart area (from PerfScatter)
        svg.selectAll("defs").remove();
        svg.append("defs")
            .append("clipPath")
            .attr("id", "chart-clip")
            .append("rect")
            .attr("x", margin.left)
            .attr("y", margin.top)
            .attr("width", chartWidth)
            .attr("height", chartHeight);
    }

    // Draw target spline line
    function drawSpline(data: any[]): void {
        logDebug('AdvancedScatter: drawSpline called', {
            dataLength: data?.length || 0,
            dataType: typeof data,
            isArray: Array.isArray(data),
            hasChartRef: !!chartRef,
            firstFewPoints: data?.slice(0, 3) || []
        });
        
        let chart = d3.select(chartRef).select("svg").select("g");
        if (data != undefined && data.length > 0) {
            // Use all data - clipping path will hide parts outside bounds
            let valueline = d3.line<any>()
                .curve(d3.curveMonotoneX)
                .x((d: any) => xScale(d.X))
                .y((d: any) => yScale(d.Y))
                .defined((d: any) => d.X != null && d.Y != null && !isNaN(d.X) && !isNaN(d.Y));

            const pathData = valueline(data);
            logDebug('AdvancedScatter: Target path generated', {
                pathData: pathData,
                pathLength: pathData?.length || 0,
                dataPoints: data.length,
                xScaleDomain: xScale.domain(),
                yScaleDomain: yScale.domain()
            });

            const targetStroke = props.targetColor || '#00ff7f';
            const targetPath = chart.append("path")
                .datum(data)
                .attr("d", pathData)
                .attr("class", "tgt")
                .style("fill", "none")
                .style("stroke", targetStroke)
                .style("stroke-width", 2)
                .style("pointer-events", "none")
                .attr("clip-path", "url(#chart-clip)");
            
            // Ensure target line is on top by raising it
            targetPath.raise();
            
            logDebug('AdvancedScatter: Target path element created', {
                elementExists: !!targetPath.node(),
                className: targetPath.attr("class")
            });
        } else {
            logError('AdvancedScatter: drawSpline called with invalid data', {
                dataLength: data?.length || 0,
                dataType: typeof data,
                isArray: Array.isArray(data),
                data: data
            });
        }
    }

    /** The overlays actually worth drawing: a slot with nothing selected carries no fit. */
    function activeBaselines(): BaselineOverlay[] {
        const overlays = props.baselines;
        if (!Array.isArray(overlays)) { return []; }
        return overlays.filter((o) => o && o.fit && o.config);
    }

    /**
     * Points for one baseline overlay on this series, as one or more strokes.
     *
     * Parametric in TWS: the reducer walks the fit's wind-speed grid and reads this chart's own x
     * and y channels at their own aggregate types, so the curve is correct even when neither axis
     * is TWS. Multiple segments mean the baseline's coverage has a gap, which is drawn as a break
     * rather than bridged.
     */
    function baselineSegmentsForSeries(overlay: BaselineOverlay): BaselineSegment[] {
        return buildBaselineSegments({
            fit: overlay.fit,
            config: overlay.config,
            // null means the page is not split by leg, so both legs are drawn.
            leg: legKeyFromUwDw(props.uwDw),
            xChannel: props.xaxis,
            yChannel: props.yaxis,
            xType: props.xType,
            yType: props.yType,
        });
    }

    /**
     * Draw every baseline curve. Mirrors drawSpline (the target line) but with class `bl`, each
     * overlay's own colour, and one path per segment.
     *
     * Deliberately does NOT extend the chart's axis domain to fit the curves: a reference should
     * never rescale the chart away from the runs being looked at, so anything outside the data's
     * own range is simply clipped.
     */
    function drawBaselineSplines(): ReferenceCurve[] {
        const drawn: ReferenceCurve[] = [];
        const chart = d3.select(chartRef).select("svg").select("g");
        // Idempotent: a partial redraw can call this again without stacking duplicate paths. One
        // clear for all overlays, so a slot that was just emptied leaves no orphaned curve behind.
        chart.selectAll("path.bl").remove();

        for (const overlay of activeBaselines()) {
            const segments = baselineSegmentsForSeries(overlay);
            if (segments.length === 0) { continue; }

            const baselineStroke = overlay.color || '#ffffff';
            drawn.push({
                name: overlay.name || 'BASELINE',
                color: baselineStroke,
                points: segments.flat(),
            });

            for (const segment of segments) {
                // curveMonotoneX needs ascending x; the reducer emits grid order, which is ascending
                // in TWS but not necessarily in an arbitrary x channel.
                const sorted = [...segment].sort((a, b) => a.X - b.X);
                const valueline = d3.line<{ X: number; Y: number }>()
                    .curve(d3.curveMonotoneX)
                    .x((d) => xScale(d.X))
                    .y((d) => yScale(d.Y))
                    .defined((d) => d.X != null && d.Y != null && !isNaN(d.X) && !isNaN(d.Y));

                chart.append("path")
                    .datum(sorted)
                    .attr("d", valueline(sorted))
                    .attr("class", "bl legend-baseline-curve")
                    .style("fill", "none")
                    .style("stroke", baselineStroke)
                    .style("stroke-width", 2)
                    .style("pointer-events", "none")
                    .attr("clip-path", "url(#chart-clip)")
                    .raise();
            }
        }
        return drawn;
    }

    // Draw cloud area visualization (from PerfScatter, conditional on cloud prop)
    function removeCloudArea(): void {
        if (!chartRef) return;
        const svg = d3.select(chartRef).select("svg");
        if (svg.empty()) return;
        
        // Remove cloud area from multiple possible locations
        svg.selectAll(".cloud-area").remove();
        svg.selectAll("g.cloud-area").remove();
        const chartbody = svg.select("g");
        if (!chartbody.empty()) {
            chartbody.selectAll(".cloud-area").remove();
            chartbody.selectAll("g.cloud-area").remove();
        }
    }

    /**
     * Draw cloud area visualization using ONLY cloud data (1Hz data from props.cloud)
     * This function does NOT use aggregate data - it relies solely on cloud_data_r
     * which is derived from props.cloud and contains only 1Hz point data
     */
    function drawArea(data: any[]): void {
        try {
            if (!data || !Array.isArray(data) || data.length === 0) {
                return;
            }

            // This function only uses cloud data - data parameter should be cloud_data_r
            // which contains only 1Hz cloud data, not aggregates
            // Debug: Log cloud data count to verify it's 1Hz data (should be many more points than aggregates)
            logDebug('AdvancedScatter: drawArea called with cloud data', {
                totalCloudPoints: data.length,
                uniqueEventIds: new Set(data.map((d: any) => d.event_id)).size,
                pointsPerEvent: new Set(data.map((d: any) => d.event_id)).size > 0 
                    ? (data.length / new Set(data.map((d: any) => d.event_id)).size).toFixed(2) 
                    : 'N/A',
                samplePoint: data.length > 0 ? {
                    event_id: data[0].event_id,
                    X: data[0].X,
                    Y: data[0].Y,
                    Datetime: data[0].Datetime
                } : null
            });

            let chartbody = d3.select(chartRef).select("svg").select("g");
            // Increased bandwidth to create more connected, flowing contours instead of circular blobs
            let bandwidth = 15;
            if (props.zoom) {
                bandwidth = 25;
            }

            let xfiltered = data.filter((d: any) => d.X > xMin && d.X < xMax);
            let yfiltered = xfiltered.filter((d: any) => d.Y > yMin && d.Y < yMax);
            
            // Debug: Log filtered counts
            logDebug('AdvancedScatter: Cloud data after bounds filtering', {
                originalCount: data.length,
                afterXFilter: xfiltered.length,
                afterYFilter: yfiltered.length,
                xBounds: [xMin, xMax],
                yBounds: [yMin, yMax]
            });

            let densityData = d3.contourDensity<any>()
                .x(function (d: any) { return xScale(d.X); })
                .y(function (d: any) { return yScale(d.Y); })
                .size([chartWidth, chartHeight])
                .bandwidth(bandwidth)
                .thresholds(20)(yfiltered);

            let dExtent = d3.extent(densityData, function (p: any) { return +p.value });

            let color: d3.ScaleLinear<number, string, never>;
            if (isDark()) {
                color = d3.scaleLinear<number, string, never>()
                    .domain([dExtent[0] || 0, (dExtent[1] || 0) * 0.6])
                    // @ts-ignore
                    // .range(["#9ca3af", "#ffffff"]);
                    .range(["#9ca3af", "lightgrey"]);
            } else {
                color = d3.scaleLinear<number, string, never>()
                    .domain([dExtent[0] || 0, (dExtent[1] || 0) * 0.6])
                    // @ts-ignore
                    .range(["#f3f4f6", "#6b7280"]);
            }

            const minOpacity = 0.1;
            const maxOpacity = 0.3;

            // Remove any existing cloud before drawing new one
            chartbody.selectAll("g.cloud-area").remove();
            
            chartbody.insert("g", "g")
                .attr("class", "cloud-area")
                .style("mix-blend-mode", "multiply")
                .style("pointer-events", "none")
                .selectAll("path")
                .data(densityData)
                .enter().append("path")
                .attr("d", d3.geoPath())
                .attr("fill", function (d: any) { return color(d.value); })
                .attr("fill-opacity", function (d: any) {
                    const v0 = dExtent[0] || 0;
                    const v1 = (dExtent[1] || 0) * 0.6;
                    const t = Math.max(0, Math.min(1, (d.value - v0) / Math.max(1e-6, (v1 - v0))));
                    return minOpacity + t * (maxOpacity - minOpacity);
                })
                .attr("stroke", "none")
                .style("pointer-events", "none");
        } catch {
            // Silently fail if cloud rendering fails
        }
    }

    // Get tooltip content — date/time come pre-formatted in local time from performanceDataService / fleetPerformanceDataService
    const getTooltipContent = (point: any): string => {
        if (!point) return "";
        const dateString = point.dateStringLocal ?? "N/A";
        const timeString = point.timeStringLocal ?? "N/A";

        // Support dynamic color field (PerfScatter) - single nomenclature: resolve via mapping
        const colorByField = props.color || 'TACK';
        let colorByValue = resolveDataField(point, colorByField);
        if (colorByValue === undefined || colorByValue === null) colorByValue = 'N/A';
        if (isRaceColorMode(colorByField) && (colorByValue === -1 || colorByValue === '-1')) colorByValue = 'TRAINING';
        if (colorByField === 'LEG' && (colorByValue === -1 || colorByValue === '-1' || String(colorByValue).toLowerCase() === 'training')) colorByValue = 'TRAINING';
        // Show source_name in uppercase in tooltip
        const isSourceField = String(colorByField).toLowerCase() === 'source_name' || String(colorByField).toLowerCase() === 'source';
        const pointVmgValue = Number(point.vmg_perc);
        const displayColorByValue = colorByField === PERFORMANCE_DENSITY_COLOR
            ? `${Math.round((pointDensity.get(point as object) ?? 0) * 100)}% of max`
            : colorByField === PERFORMANCE_VMG_COLOR
            ? (point.vmg_perc != null && Number.isFinite(pointVmgValue) ? `${pointVmgValue.toFixed(1)}%` : 'N/A')
            : isSourceField && typeof colorByValue === 'string' ? colorByValue.toUpperCase() : colorByValue;

        let tooltipRows = `
            <tr><td>DATE</td><td>${dateString}</td></tr>
            <tr><td>TIME</td><td>${timeString}</td></tr>`;
        
        tooltipRows += `
            <tr><td>${colorByField.toUpperCase()}</td><td>${displayColorByValue}</td></tr>
            <tr><td>${props.xaxis.toUpperCase()}</td><td>${parseFloat(point.X).toFixed(1)}</td></tr>
            <tr><td>${props.yaxis.toUpperCase()}</td><td>${parseFloat(point.Y).toFixed(1)}</td></tr>`;

        return `<table class='table-striped'>${tooltipRows}</table>`;
    };

    // Filter selection helper
    const filterSelection = (data: any[]): any[] => {
        const selected = domainSelectedEvents();
        return data.filter((d: any) => selected.includes(d.ID));
    };

    // Compute fit data with unified logic supporting both source-based and groups-based
    // bandwidth: loess smoothing factor (see LOESS_BANDWIDTH_*), used when point count > 15
    // Build per-group fit inputs (sorted x/y points + which curve to use). The loess/log regression runs
    // off the main thread in drawFit via the @nodets pool (see ./scatterRegressionTask.ts).
    function buildFitInputs(data: any[]): any[] {
        const fitData: any[] = [];
        let dataForFit = data;
        const currentSelection = domainSelectedEvents();
        if (currentSelection && currentSelection.length > 0) {
            const selectedIdSet = new Set(currentSelection);
            dataForFit = data.filter((d: any) => selectedIdSet.has(d.ID));
        }

        const useSourceGrouping = props.color === 'SOURCE_NAME' || props.color === 'SOURCE';
        
        // Colored by DENSITY or VMG (no categories), or by SOURCE_NAME with 'ALL' selected: fit all
        // points together as a single group.
        if (isPerformanceRampColor(props.color) || (useSourceGrouping && props.selectedSource && String(props.selectedSource).toLowerCase() === 'all')) {
            if (dataForFit.length > 0) {
                const xyValues = dataForFit.map((d: any) => ({ x: d.X, y: d.Y })).sort((a: any, b: any) => a.x - b.x);
                fitData.push({
                    group: 'ALL',
                    color: 'lightgrey',
                    xyValues,
                    useLoess: dataForFit.length > 15,
                    pointCount: dataForFit.length,
                });
            }
            return fitData;
        }

        // If colored by SOURCE_NAME, use source-based grouping
        if (useSourceGrouping) {
            const sourceGroups: Record<string, any[]> = {};
            dataForFit.forEach((d: any) => {
                const key = d.source_name || d.sourceName || d.source || d.SOURCE || d.SOURCE_NAME || 'unknown';
                const normKey = String(key).toLowerCase();
                if (!sourceGroups[normKey]) {
                    sourceGroups[normKey] = [];
                }
                sourceGroups[normKey].push(d);
            });

            Object.keys(sourceGroups).forEach((normKey: string) => {
                const groupData = sourceGroups[normKey];
                if (groupData.length > 0) {
                    const xyValues = groupData.map((d: any) => ({ x: d.X, y: d.Y })).sort((a: any, b: any) => a.x - b.x);
                    const displayName = groupData[0].SOURCE_NAME || groupData[0].sourceName || groupData[0].source || groupData[0].source_name || normKey || 'Unknown';
                    const scale = props.colorScale;
                    const color = scale && typeof scale === 'function' ? scale(normKey) : getColor({ source_name: normKey });
                    fitData.push({
                        group: displayName,
                        color: color,
                        xyValues,
                        useLoess: groupData.length > 15,
                        pointCount: groupData.length,
                    });
                }
            });
        } else {
            // For other color fields, use groups-based grouping
            if (props.groups && Array.isArray(props.groups) && props.groups.length > 0 && props.color) {
                const colorField = props.color;
                props.groups.forEach((group: { name: string; color: string }) => {
                    const groupData = dataForFit.filter((d: any) => {
                        let value = resolveDataField(d, colorField);
                        if (isRaceColorMode(colorField) && (value === -1 || value === '-1')) {
                            value = 'TRAINING';
                        }
                        const valueStr = value !== undefined && value !== null ? String(value) : null;
                        const groupNameStr = String(group.name);
                        if (isRaceColorMode(colorField) && groupNameStr === RACE_NUMBER_COLLAPSED_LEGEND_NAME) {
                            return !isTrainingRaceMetadataValue(value);
                        }
                        if (isRaceColorMode(colorField) && groupNameStr === 'TRAINING') {
                            return isTrainingRaceMetadataValue(value);
                        }
                        if (valueStr === groupNameStr) return true;
                        const valueNum = Number(valueStr);
                        const groupNum = Number(groupNameStr);
                        if (!isNaN(valueNum) && !isNaN(groupNum) && valueNum === groupNum) return true;
                        if (valueStr && groupNameStr && valueStr.toLowerCase() === groupNameStr.toLowerCase()) return true;
                        return false;
                    });
                    
                    if (groupData.length > 0) {
                        const xyValues = groupData.map((d: any) => ({ x: d.X, y: d.Y })).sort((a: any, b: any) => a.x - b.x);
                        fitData.push({
                            group: group.name,
                            color: group.color,
                            xyValues,
                            useLoess: groupData.length > 15,
                            pointCount: groupData.length,
                        });
                    }
                });
            }
        }

        return fitData;
    }

    /**
     * Clip a polyline to axis bounds in data space. Returns one or more segments
     * that lie inside [xMin, xMax] x [yMin, yMax], with intersection points at edges.
     */
    function clipPolylineToBounds(
        points: Array<{ x: number; y: number }>,
        xMin: number,
        xMax: number,
        yMin: number,
        yMax: number
    ): Array<Array<{ x: number; y: number }>> {
        if (points.length === 0) return [];
        const segments: Array<Array<{ x: number; y: number }>> = [];
        let current: Array<{ x: number; y: number }> = [];

        const inside = (p: { x: number; y: number }) =>
            p.x >= xMin && p.x <= xMax && p.y >= yMin && p.y <= yMax;

        // Clip segment (a,b) to rect; returns 0 or 1 segment (Liang-Barsky).
        // t_enter = max of entering t values, t_exit = min of exiting t values.
        const clipSegment = (
            a: { x: number; y: number },
            b: { x: number; y: number }
        ): Array<{ x: number; y: number }> | null => {
            let tEnter = 0, tExit = 1;
            const dx = b.x - a.x, dy = b.y - a.y;

            const clipEdge = (p: number, q: number): boolean => {
                if (p === 0) {
                    if (q < 0) return false; // outside and parallel
                    return true;
                }
                const t = q / p;
                if (p < 0) {
                    if (t > tExit) return false;
                    if (t > tEnter) tEnter = t;
                } else {
                    if (t < tEnter) return false;
                    if (t < tExit) tExit = t;
                }
                return true;
            };

            if (!clipEdge(-dx, a.x - xMin)) return null;
            if (!clipEdge(dx, xMax - a.x)) return null;
            if (!clipEdge(-dy, a.y - yMin)) return null;
            if (!clipEdge(dy, yMax - a.y)) return null;

            if (tEnter <= tExit) {
                const p0 = { x: a.x + tEnter * dx, y: a.y + tEnter * dy };
                const p1 = { x: a.x + tExit * dx, y: a.y + tExit * dy };
                return [p0, p1];
            }
            return null;
        };

        for (let i = 0; i < points.length; i++) {
            const p = points[i];
            if (inside(p)) {
                if (current.length === 0 && i > 0) {
                    const prev = points[i - 1];
                    const clipped = clipSegment(prev, p);
                    if (clipped && clipped.length >= 1) current.push(clipped[0]);
                }
                current.push(p);
            } else {
                if (current.length > 0) {
                    const prev = points[i - 1];
                    const clipped = clipSegment(prev, p);
                    // Segment (prev,p) goes inside->outside; clipped is [prev, exit], add exit point
                    if (clipped && clipped.length >= 2) current.push(clipped[1]);
                    segments.push(current);
                    current = [];
                }
            }
        }
        if (current.length > 0) segments.push(current);
        return segments;
    }

    /**
     * Schedules drawFit to run after the current turn, so clearing selection doesn't block the UI
     * while recomputing fits on the full dataset. Clears existing fit lines immediately.
     */
    let pendingFitTimerId: ReturnType<typeof setTimeout> | null = null;
    const scheduleDrawFit = () => {
        if (!fitsEnabled() || !chartRef?.isConnected) return;
        const chart = d3.select(chartRef).select("svg").select("g");
        if (chart.node()) chart.selectAll(".fit").remove();
        if (pendingFitTimerId != null) clearTimeout(pendingFitTimerId);
        pendingFitTimerId = setTimeout(() => {
            pendingFitTimerId = null;
            if (!isCleaningUp && chartRef?.isConnected && fitsEnabled()) drawFit();
        }, 0);
    };

    // Draw fit lines (trimmed to current x/y axis bounds)
    // Monotonic guard so a slower earlier fit can't overwrite a newer one when the selection changes fast.
    let fitDrawSeq = 0;

    // The loess/log regressions run off the main thread on a @nodets pool (per group), then we draw on
    // resolve — only if this is still the latest request and the component is still mounted.
    const drawFit = async () => {
        // Prevent execution if component is cleaning up or not connected
        if (isCleaningUp || !chartRef || !chartRef.isConnected) return;
        if (!fitsEnabled()) return;
        let chart = d3.select(chartRef).select("svg").select("g");
        if (!chart.node()) return; // Chart not ready

        // Fits use the full `filtered()` aggregate (same rows as setFiltered after draw); not the render-only subsample inside drawScatter.
        const bandwidth = loessBandwidth();
        const fitInputs = buildFitInputs(filtered());
        const mySeq = ++fitDrawSeq;
        const fitCurves = await computeScatterFitValues(
            fitInputs.map((f: any) => ({ xyValues: f.xyValues, useLoess: f.useLoess, bandwidth }))
        );
        // Bail if superseded or the component went away while the pool was working.
        if (mySeq !== fitDrawSeq || isCleaningUp || !chartRef || !chartRef.isConnected) return;
        chart = d3.select(chartRef).select("svg").select("g");
        if (!chart.node()) return;
        chart.selectAll(".fit").remove();

        const fitDataComputed = fitInputs.map((f: any, i: number) => ({
            group: f.group,
            color: f.color,
            fitValues: fitCurves[i] ?? [],
            pointCount: f.pointCount,
        }));

        const [xMinDom, xMaxDom] = xScale.domain();
        const [yMinDom, yMaxDom] = yScale.domain();
        const lineGenerator = d3.line<{ x: number; y: number }>()
            .curve(d3.curveMonotoneX)
            .x((d: { x: number; y: number }) => xScale(d.x))
            .y((d: { x: number; y: number }) => yScale(d.y));
        fitDataComputed.forEach(({ color, fitValues }: { color: string; fitValues: Array<{ x: number; y: number }> }) => {
            // Double-check before appending (component might have been unmounted during computation)
            if (isCleaningUp || !chartRef || !chartRef.isConnected) return;
            const segments = clipPolylineToBounds(fitValues, xMinDom, xMaxDom, yMinDom, yMaxDom);
            segments.forEach((segment) => {
                if (segment.length < 2) return;
                chart.append("path")
                    .datum(segment)
                    .attr("d", lineGenerator)
                    .attr("class", "fit")
                    .style("stroke", color)
                    .style("stroke-width", 2)
                    .style("pointer-events", "none");
            });
        });
        if (!isCleaningUp && chartRef && chartRef.isConnected) {
            setFitDataVersion(v => v + 1);
            setFitData(fitDataComputed);
        }
        // Fit paths are pointer-events:none, but keep the canvas hit rect topmost in the DOM.
        if (isHighDensityMode) raiseCanvasScatterHitOverlay();
    };

    function isScatterChartInViewport(): boolean {
        if (!chartRef) return false;
        const rect = chartRef.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0 && rect.top < window.innerHeight && rect.bottom > 0;
    }

    /** Attach click/hover to `.scatter` when the chart is on-screen. Covers first-load race where `isVisible()` is still false before IntersectionObserver runs. */
    function tryAttachScatterListeners(): void {
        if (!chartRef || !chartRef.isConnected || listenersAttached) return;
        if (!storedMouseover || !storedMouseout || !storedClick) return;
        const scatterPoints = d3.select(chartRef).selectAll(".scatter");
        if (scatterPoints.size() === 0) return;
        if (!isVisible() && !isScatterChartInViewport()) return;
        scatterPoints
            .on("mouseover", storedMouseover)
            .on("mouseout", storedMouseout)
            .on("click", storedClick);
        listenersAttached = true;
    }

    // ========================================================================
    // High-density canvas scatter renderer
    // ------------------------------------------------------------------------
    // Visual points are painted on a <canvas> sibling of the SVG (pointer-events:
    // none). All interaction (tooltip, click-select, lasso) runs through a single
    // transparent SVG hit rect on the plot <g>; selection still flows through the
    // existing selection store + lasso worker.
    // ========================================================================

    /** Tooltip position relative to the scroll container (mirrors the SVG-mode helper). */
    function getScatterTooltipPosition(event: MouseEvent): { x: number; y: number } {
        try {
            if (!containerRef) return { x: 0, y: 0 };
            const rect = containerRef.getBoundingClientRect();
            const relX = event.clientX - rect.left;
            const relY = event.clientY - rect.top;
            const scrollX = containerRef.scrollLeft || 0;
            const scrollY = containerRef.scrollTop || 0;
            return { x: relX - 100 + scrollX, y: relY + scrollY };
        } catch {
            return { x: 0, y: 0 };
        }
    }

    function getScatterCanvasElement(): HTMLCanvasElement | null {
        if (!chartRef) return null;
        return chartRef.querySelector(`canvas.${CANVAS_POINTS_CLASS}`) as HTMLCanvasElement | null;
    }

    function removeCanvasElement(): void {
        const canvas = getScatterCanvasElement();
        if (canvas) canvas.remove();
    }

    /** True when the chart currently has points drawn in either renderer. */
    function chartHasRenderedPoints(): boolean {
        if (!chartRef) return false;
        if (d3.select(chartRef).selectAll(".scatter").size() > 0) return true;
        return !!getScatterCanvasElement();
    }

    /** Ensure the canvas exists immediately before the SVG so it sits under the overlays. */
    function getOrCreateScatterCanvas(): HTMLCanvasElement | null {
        if (!chartRef) return null;
        const svgNode = d3.select(chartRef).select("svg").node() as SVGSVGElement | null;
        if (!svgNode) return null;
        let canvas = getScatterCanvasElement();
        if (!canvas) {
            canvas = document.createElement("canvas");
            canvas.className = CANVAS_POINTS_CLASS;
            chartRef.insertBefore(canvas, svgNode);
        }
        return canvas;
    }

    /**
     * Plot-area origin relative to the container's positioning (padding) box, in local
     * layout px. Uses the SVG's layout offset (correct under CSS scaling); falls back to
     * the container padding when offsetLeft/offsetTop are unavailable.
     */
    function getPlotOffsetWithinContainer(svgNode: SVGSVGElement): { left: number; top: number } {
        const anySvg = svgNode as unknown as { offsetLeft?: number; offsetTop?: number };
        let baseLeft = anySvg.offsetLeft;
        let baseTop = anySvg.offsetTop;
        if (!Number.isFinite(baseLeft) || !Number.isFinite(baseTop)) {
            // SVG is the first content child, so its offset equals the container padding.
            const cs = chartRef && typeof getComputedStyle === "function" ? getComputedStyle(chartRef) : null;
            baseLeft = cs ? (parseFloat(cs.paddingLeft) || 0) : 0;
            baseTop = cs ? (parseFloat(cs.paddingTop) || 0) : 0;
        }
        return { left: (baseLeft as number) + margin.left, top: (baseTop as number) + margin.top };
    }

    /** Point radius / square size given selection state (matches updatePointSizesAndOpacity). */
    function canvasPointGeometry(
        d: any,
        isSelected: boolean,
        hasSelection: boolean
    ): { r: number; rectSize: number } {
        return scatterPointGeometry(d, isSelected, hasSelection, false);
    }

    /** Paint every point onto the canvas, matching SVG colors, shapes, sizes and selection dimming. */
    function renderPointsCanvas(data: any[]): void {
        if (!chartRef) return;
        const svgNode = d3.select(chartRef).select("svg").node() as SVGSVGElement | null;
        if (!svgNode) return;
        const canvas = getOrCreateScatterCanvas();
        if (!canvas) return;

        const dpr = (typeof window !== "undefined" && window.devicePixelRatio) || 1;
        // Match the plot area exactly: same left/top as the plot <g>, same width/height.
        const { left, top } = getPlotOffsetWithinContainer(svgNode);
        canvas.style.left = `${left}px`;
        canvas.style.top = `${top}px`;
        canvas.style.width = `${chartWidth}px`;
        canvas.style.height = `${chartHeight}px`;
        canvas.width = Math.max(1, Math.round(chartWidth * dpr));
        canvas.height = Math.max(1, Math.round(chartHeight * dpr));

        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, chartWidth, chartHeight);

        const strokeColor = getThemeStrokeColor();
        const fill = getFillColor ?? ((d: any) => getColor(d));
        const selected = domainSelectedEvents();
        const hasSelection = selected.length > 0;
        const selectedSet = hasSelection ? new Set(selected) : null;

        // Display Options → Highlight Fits & Targets (the SVG path gets the same from Styles.css).
        const highlightLines = performanceDisplayStore.highlightScatterLines();
        const sizeScale = highlightLines ? HIGHLIGHT_LINES_POINT_SCALE : 1;
        const alphaScale = highlightLines ? HIGHLIGHT_LINES_POINT_ALPHA : 1;

        ctx.lineWidth = sizeScale;
        ctx.strokeStyle = strokeColor;
        for (const d of data) {
            const x = xScale(Number(d.X));
            const y = yScale(Number(d.Y));
            if (!Number.isFinite(x) || !Number.isFinite(y)) continue;

            const id = d.ID;
            const isSelected = !!(selectedSet && id !== undefined && id !== null && selectedSet.has(id));
            ctx.globalAlpha = getScatterPointOpacity(d, selected) * alphaScale;
            ctx.fillStyle = fill(d) || "lightgrey";
            const geometry = canvasPointGeometry(d, isSelected, hasSelection);
            const r = geometry.r * sizeScale;
            const rectSize = geometry.rectSize * sizeScale;

            const tack = (resolveDataField(d, "TACK") ?? d.tack);
            ctx.beginPath();
            if (tack === "PORT") {
                // SVG rects anchor their top-left at the data point (not centered); match that.
                ctx.rect(x, y, rectSize, rectSize);
            } else {
                ctx.arc(x, y, r, 0, Math.PI * 2);
            }
            ctx.fill();
            ctx.stroke();
        }
        ctx.globalAlpha = 1;
    }

    /** Remove the canvas hit rect + lasso visual and drop the lasso instance. */
    function teardownCanvasScatterInteraction(): void {
        if (!chartRef) return;
        const svg = d3.select(chartRef).select("svg");
        if (svg.empty()) return;
        svg.selectAll(".lasso").remove();
        svg.selectAll(`.${CANVAS_HIT_AREA_CLASS}`).remove();
        canvasLassoInstance = null;
    }

    /** Keep the transparent hit rect (and lasso visual) above grid / target / fit overlays. */
    function raiseCanvasScatterHitOverlay(): void {
        if (!chartRef) return;
        const svg = d3.select(chartRef).select("svg");
        if (svg.empty()) return;
        const hit = svg.select(`.${CANVAS_HIT_AREA_CLASS}`);
        if (!hit.empty()) hit.raise();
        const lassoG = svg.select(".lasso");
        if (!lassoG.empty()) lassoG.raise();
    }

    /**
     * Shared lasso worker call: takes points (client coords) + polygon (client coords),
     * resolves selected IDs and merges them into the selection store. Used by both the
     * SVG and canvas renderers.
     */
    async function computeAndApplyLassoSelection(
        pointData: Array<{ id: string; x: number; y: number }>,
        drawnCoords: [number, number][],
        data: any[],
    ): Promise<void> {
        if (pointData.length === 0) return;
        if (!lassoWorker) {
            // Use ?worker import - Vite bundles this correctly in production
            lassoWorker = new LassoSelectionWorker();
            if (!lassoWorker) throw new Error('Failed to create lasso worker');
        }
        const messageId = `lasso-selection-${Date.now()}-${Math.random()}`;
        const polygonLen = Array.isArray(drawnCoords) ? drawnCoords.length : 0;
        const timeoutMs = Math.min(
            120_000,
            Math.max(20_000, 12_000 + pointData.length * 8 + polygonLen * 40)
        );
        const selectedIds = await new Promise<string[]>((resolve, reject) => {
            const timeout = setTimeout(() => {
                if (lassoWorker) lassoWorker.removeEventListener('message', handleMessage);
                reject(new Error(`Lasso selection computation timeout after ${timeoutMs}ms (${pointData.length} points, ${polygonLen} polygon vertices)`));
            }, timeoutMs);
            const handleMessage = (event: MessageEvent) => {
                if (event.data.id === messageId) {
                    clearTimeout(timeout);
                    if (lassoWorker) lassoWorker.removeEventListener('message', handleMessage);
                    if (event.data.type === 'success' && event.data.result) {
                        resolve(event.data.result.selectedIds);
                    } else {
                        reject(new Error(event.data.error || 'Unknown error'));
                    }
                }
            };
            if (lassoWorker) {
                lassoWorker.addEventListener('message', handleMessage);
                lassoWorker.postMessage({
                    id: messageId,
                    type: 'COMPUTE_LASSO_SELECTION',
                    data: { points: pointData, polygon: drawnCoords },
                    timestamp: Date.now()
                });
            } else {
                clearTimeout(timeout);
                reject(new Error('Worker not available'));
            }
        });
        if (selectedIds.length > 0) {
            const numericIds = selectedIds.map(id => Number(id)).filter(id => !isNaN(id));
            const currentSelected = domainSelectedEvents();
            const currentSelectedIds = new Set(currentSelected);
            const allIds = new Set([...currentSelectedIds, ...numericIds]);
            setDomainSelectedEvents(Array.from(allIds).map(id => Number(id)));
            setFiltered(filterSelection(data));
            setTriggerSelection(true);
            setHasSelection(allIds.size > 0);
        }
    }

    /**
     * Set up interaction for the canvas renderer: a transparent hit rect over the plot
     * area drives the nearest-point tooltip, click-to-toggle selection and the lasso.
     * Must be called after the SVG plot <g> exists; safe to call repeatedly.
     */
    function setupCanvasScatterInteraction(data: any[]): void {
        if (!chartRef) return;
        const svg = d3.select(chartRef).select("svg");
        if (svg.empty()) return;
        const chartbody = svg.select<SVGGElement>("g");
        if (chartbody.empty()) return;
        const plotG = chartbody.node();
        if (!plotG) return;

        teardownCanvasScatterInteraction();

        const hitRect = chartbody.append("rect")
            .attr("class", CANVAS_HIT_AREA_CLASS)
            .attr("x", 0)
            .attr("y", 0)
            .attr("width", chartWidth)
            .attr("height", chartHeight);

        const shouldDisableHover = () => isLassoActive || isComputingSelection;

        hitRect.on("mousemove.canvas-scatter", (event: MouseEvent) => {
            if (shouldDisableHover()) return;
            const [px, py] = d3.pointer(event, plotG);
            const near = findNearestScatterPoint(data, px, py, xScale, yScale, CANVAS_HIT_RADIUS_PX);
            if (near) {
                const position = getScatterTooltipPosition(event);
                setTooltip({ visible: true, content: getTooltipContent(near), x: position.x, y: position.y });
            } else {
                setTooltip({ visible: false, content: "", x: 0, y: 0 });
            }
        });

        hitRect.on("mouseout.canvas-scatter", () => {
            setTooltip({ visible: false, content: "", x: 0, y: 0 });
        });

        hitRect.on("click.canvas-scatter", (event: MouseEvent) => {
            event.stopPropagation();
            // Suppress the click that ends a lasso drag.
            if (isLassoActive && canvasLassoInstance) {
                const drawnCoords = canvasLassoInstance.getDrawnCoords();
                if (drawnCoords && drawnCoords.length >= 2) return;
            }
            if (shouldDisableHover() || !allowclick) return;
            const [px, py] = d3.pointer(event, plotG);
            const near = findNearestScatterPoint(data, px, py, xScale, yScale, CANVAS_HIT_RADIUS_PX);
            if (!near || near.ID === undefined || near.ID === null) return;
            allowclick = false;
            const list = domainSelectedEvents();
            const exists = list.includes(near.ID);
            const nextList = exists ? list.filter(id => id !== near.ID) : [...list, near.ID];
            setDomainSelectedEvents(nextList);
            setFiltered(filterSelection(data));
            setTriggerSelection(true);
            setHasSelection(nextList.length > 0);
            renderPointsCanvas(data);
            allowclick = true;
        });

        // Lasso: drag over the hit rect, polygon drawn in plot <g> space. The worker uses
        // client-space points (via getScreenCTM) to match the client-space polygon.
        try {
            const lassoInstance = lasso()
                .closePathSelect(true)
                // @ts-ignore
                .closePathDistance(75)
                .items(svg.selectAll(".__canvas-scatter-no-items"))
                .targetArea(hitRect)
                .skipDragCalculations(true)
                .on('start', () => {
                    isLassoActive = true;
                    isComputingSelection = false;
                    setTooltip({ visible: false, content: "", x: 0, y: 0 });
                })
                .on('end', async () => {
                    isLassoActive = false;
                    const drawnCoords = lassoInstance.getDrawnCoords();
                    if (!drawnCoords || drawnCoords.length < 3) return;

                    isComputingSelection = true;
                    setIsComputingLassoSelection(true);
                    setLassoComputingCursor(true);
                    try {
                        const node = chartbody.node();
                        if (!node) return;
                        const pointData = buildLassoScreenPoints(data, node, xScale, yScale);
                        await computeAndApplyLassoSelection(pointData, drawnCoords, data);
                    } catch (error: any) {
                        logError('Error computing canvas lasso selection:', error);
                    } finally {
                        isComputingSelection = false;
                        setIsComputingLassoSelection(false);
                        setLassoComputingCursor(false);
                    }
                });
            chartbody.call(lassoInstance);
            canvasLassoInstance = lassoInstance;
        } catch (error: any) {
            logError('Error setting up canvas lasso:', error);
        }

        raiseCanvasScatterHitOverlay();
    }

    // Draw scatter points - simplified but complete version.
    // When N > maxPoints, DOM uses a density-aware subsample (see sampleScatterForRender). Lasso only sees rendered nodes, same as prior stride cap.
    /** Color By DENSITY: per-point heat in pixel space on the full data (before the render sample), so a zoom recomputes it. */
    function refreshPointDensity(data: any[]): void {
        pointDensity = new WeakMap();
        if (props.color !== PERFORMANCE_DENSITY_COLOR) {return;}
        const fractions = pointDensityFractions(
            data.map((d) => ({ x: xScale(d.X), y: yScale(d.Y) })),
            DENSITY_CELL_PX
        );
        data.forEach((d, i) => {
            if (d && typeof d === 'object') {pointDensity.set(d, fractions[i] ?? 0);}
        });
    }

    /** Color By VMG: each point's VMG % placed on this chart's range (see vmgFractions). */
    function refreshPointVmg(data: any[]): void {
        if (props.color !== PERFORMANCE_VMG_COLOR) {
            pointVmg = new WeakMap();
            return;
        }
        pointVmg = vmgFractions(data as object[], (d) => {
            const v = (d as Record<string, unknown>).vmg_perc;
            return v == null ? undefined : Number(v);
        });
    }

    /** DENSITY: sparse → dense; VMG: low → high (no VMG first, grey underneath). Others unchanged. */
    function orderForRampDrawOrder(data: any[]): any[] {
        if (props.color === PERFORMANCE_DENSITY_COLOR) {
            const heat = (d: any): number => pointDensity.get(d) ?? 0;
            return [...data].sort((a, b) => heat(a) - heat(b));
        }
        if (props.color === PERFORMANCE_VMG_COLOR) {
            const heat = (d: any): number => pointVmg.get(d) ?? -1;
            return [...data].sort((a, b) => heat(a) - heat(b));
        }
        return data;
    }

    function drawScatter(data: any[]): void {
        refreshPointDensity(data);
        refreshPointVmg(data);
        data = orderForRampDrawOrder(data);
        try {
            let svg = d3.select(chartRef).select("svg");
            let chartbody = d3.select(chartRef).select("svg").select("g");

            const setMouseID = props.setMouseID;

            let lassoInstance: any = null;

            getFillColor = (d: any) => getColor(d);
            const strokeColor = getThemeStrokeColor();

            // High-density branch: paint all points on the canvas layer and wire the canvas
            // hit rect for tooltip / click / lasso. The SVG point + lasso path below is skipped.
            isHighDensityMode = shouldUseHighDensityCanvasScatter(data.length);
            if (isHighDensityMode) {
                canvasScatterData = data;
                storedMouseover = null;
                storedMouseout = null;
                storedClick = null;
                listenersAttached = false;
                renderPointsCanvas(data);
                setupCanvasScatterInteraction(data);
                return;
            }
            // Below threshold: ensure no stale canvas/hit rect lingers, then draw SVG points.
            teardownCanvasScatterInteraction();
            removeCanvasElement();
            canvasScatterData = [];

            const maxPoints = 3200;
            const xDom = xScale.domain() as [number, number];
            const yDom = yScale.domain() as [number, number];
            const selectedForSample = domainSelectedEvents() ?? [];
            let processedData = data;
            if (data.length > maxPoints) {
                logDebug('AdvancedScatter: density-aware scatter render sample', {
                    input: data.length,
                    maxPoints,
                    xDomain: xDom,
                    yDomain: yDom,
                    selectedCount: selectedForSample.length
                });
                processedData = sampleScatterForRender(
                    data as ScatterRenderPoint[],
                    maxPoints,
                    xDom,
                    yDom,
                    selectedForSample
                );
                logDebug('AdvancedScatter: scatter render sample result', { output: processedData.length });
                processedData = orderForRampDrawOrder(processedData);
            }

            const isHighlighted = (d: any) => {
                const selected = domainSelectedEvents();
                return selected && selected.length > 0 && d.ID !== undefined && d.ID !== null && selected.includes(d.ID);
            };

            const data_stbd = processedData.filter((d) => (resolveDataField(d, 'TACK') ?? d.tack) === 'STBD');
            const data_stbd_regular = data_stbd.filter((d) => !isHighlighted(d));
            const data_stbd_highlighted = data_stbd.filter((d) => isHighlighted(d));
            const data_port = processedData.filter((d) => (resolveDataField(d, 'TACK') ?? d.tack) === 'PORT');
            const data_port_regular = data_port.filter((d) => !isHighlighted(d));
            const data_port_highlighted = data_port.filter((d) => isHighlighted(d));

            // Helper to update hover state
            const updateAllChartsHover = (hoverID: number | null): void => {
                const hasSVG = chartRef && d3.select(chartRef).select("svg").node();
                if (!chartRef || (!isVisible() && !hasSVG && !isLassoActive)) return;
                const selected = domainSelectedEvents();
                const hasSelection = selected.length > 0;
                const scatterPoints = d3.select(chartRef).selectAll(".scatter");
                scatterPoints.filter("circle")
                    .transition().duration(200)
                    .attr("r", (d: any) => {
                        if (!d || d.ID === undefined || d.ID === null) return 5;
                        const isSelected = hasSelection && selected.includes(d.ID);
                        const isHovered = hoverID && d.ID === hoverID;
                        return scatterPointGeometry(d, isSelected, hasSelection, !!isHovered).r;
                    });
                scatterPoints.filter("rect")
                    .transition().duration(200)
                    .attr("width", (d: any) => {
                        if (!d || d.ID === undefined || d.ID === null) return 8.25;
                        const isSelected = hasSelection && selected.includes(d.ID);
                        const isHovered = hoverID && d.ID === hoverID;
                        return scatterPointGeometry(d, isSelected, hasSelection, !!isHovered).rectSize;
                    })
                    .attr("height", (d: any) => {
                        if (!d || d.ID === undefined || d.ID === null) return 8.25;
                        const isSelected = hasSelection && selected.includes(d.ID);
                        const isHovered = hoverID && d.ID === hoverID;
                        return scatterPointGeometry(d, isSelected, hasSelection, !!isHovered).rectSize;
                    });
                if (isLiveRecencyOpacityEnabled()) {
                    applyScatterOpacityToChart();
                }
            }

            const getTooltipPosition = (event: MouseEvent) => {
                try {
                    if (!containerRef) return { x: 0, y: 0 };
                    const rect = containerRef.getBoundingClientRect();
                    const relX = event.clientX - rect.left;
                    const relY = event.clientY - rect.top;
                    const scrollX = containerRef.scrollLeft || 0;
                    const scrollY = containerRef.scrollTop || 0;
                    return { x: relX - 100 + scrollX, y: relY + scrollY };
                } catch {
                    return { x: 0, y: 0 };
                }
            };

            const shouldDisableHover = () => isLassoActive || isComputingSelection;

            const mouseover = (event: MouseEvent, d: any) => {
                if (shouldDisableHover()) return;
                if (setMouseID) setMouseID(d.ID);
                updateAllChartsHover(d.ID);
                const tooltipContent = getTooltipContent(d);
                const position = getTooltipPosition(event);
                setTooltip({ visible: true, content: tooltipContent, x: position.x, y: position.y });
            };

            const mouseout = () => {
                if (shouldDisableHover()) return;
                if (setMouseID) setMouseID(null);
                updateAllChartsHover(null);
                setTooltip({ visible: false, content: "", x: 0, y: 0 });
            };

            const click = function (event: MouseEvent, d: any) {
                event.stopPropagation();
                event.preventDefault();
                if (isLassoActive && lassoInstance) {
                    const drawnCoords = lassoInstance.getDrawnCoords();
                    if (drawnCoords && drawnCoords.length >= 2) return;
                }
                if (shouldDisableHover() || !allowclick) return;
                allowclick = false;
                const list = domainSelectedEvents();
                const exists = list.includes(d.ID);
                const nextList = exists ? list.filter(id => id !== d.ID) : [...list, d.ID];
                setDomainSelectedEvents(nextList);
                setFiltered(filterSelection(data));
                setTriggerSelection(true);
                setHasSelection(nextList.length > 0);
                allowclick = true;
            };

            storedMouseover = mouseover;
            storedMouseout = mouseout;
            storedClick = click;

            // Draw regular points first, then highlighted points on top
            chartbody.append("g").selectAll("circle").data(data_stbd_regular).enter().append("circle")
                .attr("class", "scatter")
                .style("stroke", strokeColor)
                .style("fill", (d: any) => getFillColor?.(d) || "lightgrey")
                .attr("r", 5)
                .attr("cx", (d: any) => xScale(d.X))
                .attr("cy", (d: any) => yScale(d.Y));

            chartbody.append("g").selectAll("rect").data(data_port_regular).enter().append("rect")
                .attr("class", "scatter")
                .style("stroke", strokeColor)
                .style("fill", (d: any) => getFillColor?.(d) || "lightgrey")
                .attr("width", 8.25).attr("height", 8.25)
                .attr("x", (d: any) => xScale(d.X))
                .attr("y", (d: any) => yScale(d.Y));

            chartbody.append("g").selectAll("circle").data(data_stbd_highlighted).enter().append("circle")
                .attr("class", "scatter")
                .style("stroke", strokeColor)
                .style("fill", (d: any) => getFillColor?.(d) || "lightgrey")
                .attr("r", 6)
                .attr("cx", (d: any) => xScale(d.X))
                .attr("cy", (d: any) => yScale(d.Y));

            chartbody.append("g").selectAll("rect").data(data_port_highlighted).enter().append("rect")
                .attr("class", "scatter")
                .style("stroke", strokeColor)
                .style("fill", (d: any) => getFillColor?.(d) || "lightgrey")
                .attr("width", 11).attr("height", 11)
                .attr("x", (d: any) => xScale(d.X))
                .attr("y", (d: any) => yScale(d.Y));

            const points = chartbody.selectAll(".scatter");
            tryAttachScatterListeners();
            requestAnimationFrame(() => tryAttachScatterListeners());

            // Setup lasso
            try {
                if (!svg || !svg.node()) return;
                const lassoInstance = lasso()
                    .closePathSelect(true)
                    // @ts-ignore
                    .closePathDistance(75)
                    .items(points)
                    .targetArea(svg)
                    .skipDragCalculations(true)
                    .on('start', () => {
                        isLassoActive = true;
                        isComputingSelection = false;
                        if (setMouseID) setMouseID(null);
                        updateAllChartsHover(null);
                        setTooltip({ visible: false, content: "", x: 0, y: 0 });
                    })
                    .on('end', async () => {
                        isLassoActive = false;
                        const drawnCoords = lassoInstance.getDrawnCoords();
                        if (!drawnCoords || drawnCoords.length < 3) return;

                        isComputingSelection = true;
                        setIsComputingLassoSelection(true);
                        setLassoComputingCursor(true);
                        try {
                            const scatterData = points.data() as any[];
                            const scatterNodes = points.nodes() as Array<Element | null>;
                            const pointData: Array<{ id: string; x: number; y: number }> = [];
                            const len = Math.min(scatterData.length, scatterNodes.length);
                            for (let i = 0; i < len; i++) {
                                const d = scatterData[i];
                                if (!d || d.ID === undefined || d.ID === null) continue;
                                const node = scatterNodes[i];
                                if (!node) continue;
                                const box = (node as Element).getBoundingClientRect();
                                pointData.push({
                                    id: String(d.ID),
                                    x: box.left + box.width / 2,
                                    y: box.top + box.height / 2
                                });
                            }
                            if (pointData.length === 0) return;
                            await computeAndApplyLassoSelection(pointData, drawnCoords, data);
                        } catch (error: any) {
                            logError('Error computing lasso selection:', error);
                        } finally {
                            isComputingSelection = false;
                            setIsComputingLassoSelection(false);
                            setLassoComputingCursor(false);
                        }
                    });
                svg.call(lassoInstance);
                const allScatterPoints = chartbody.selectAll(".scatter");
                allScatterPoints.on('mousedown', function (event: MouseEvent) {
                    event.stopPropagation();
                });
            } catch (error: any) {
                logError('Error setting up lasso:', error);
            }
        } catch (error: any) {
            logError('Error in drawScatter:', error);
        }
    }

    // Main draw chart function
    const drawChart = (capturedAggregates?: any[], capturedCloud?: any[]) => {
        if (!chartRef || !chartRef.isConnected) return;
        const hasSVG = chartRef && d3.select(chartRef).select("svg").node();
        if (!isVisible() && hasSVG && chartRef) {
            const rect = chartRef.getBoundingClientRect();
            const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;
            if (!isInViewport) return;
        }

        // selectAll("*").remove() clears both the SVG and the canvas sibling.
        d3.select(chartRef).selectAll("*").remove();
        isLassoActive = false;
        listenersAttached = false;
        isHighDensityMode = false;
        canvasScatterData = [];
        canvasLassoInstance = null;

        const aggregatesToUse = capturedAggregates !== undefined ? capturedAggregates : (lastCapturedAggregates !== undefined ? lastCapturedAggregates : []);
        // Always use current props.cloud if capturedCloud is not provided - never use stale lastCapturedCloud
        // This ensures we always have the correct filtered cloud data (upwind or downwind) from Performance.tsx
        const cloudToUse = capturedCloud !== undefined ? capturedCloud : (props.cloud && Array.isArray(props.cloud) ? props.cloud : []);

        let aggregate_data: any[] = [];
        let cloud_data: any[] = [];
        let target_data: any = [];

        // If we have separate aggregate types, skip the old filtering logic - we'll filter them separately below
        // Otherwise, use the old filtering logic for backward compatibility
        if (!(props.aggregatesAVG || props.aggregatesSTD || props.aggregatesAAV)) {
            if (props.filters && props.filters.length > 0) {
                props.filters.forEach((filter: string) => {
                    if (filter == 'upwind') {
                        aggregate_data = filterData(aggregatesToUse, "UW", "BOTH");
                        // props.cloud is already filtered by Performance.tsx, so use it directly without re-filtering
                        cloud_data = (cloudToUse && Array.isArray(cloudToUse) && cloudToUse.length > 0) ? cloudToUse : [];
                        target_data = props.targets?.['UPWIND'] || [];
                    } else if (filter == 'downwind') {
                        aggregate_data = filterData(aggregatesToUse, "DW", "BOTH");
                        // props.cloud is already filtered by Performance.tsx, so use it directly without re-filtering
                        cloud_data = (cloudToUse && Array.isArray(cloudToUse) && cloudToUse.length > 0) ? cloudToUse : [];
                        target_data = props.targets?.['DOWNWIND'] || [];
                    }
                });
            } else {
                // When filters are empty, check uwDw prop for aggregate filtering
                // NOTE: props.cloud is already filtered by Performance.tsx based on UwDw toggle,
                // so we should NOT filter it again here - just use it directly
                if (props.uwDw === 'UPWIND') {
                    aggregate_data = filterData(aggregatesToUse, "UW", "BOTH");
                    // Use cloud directly - it's already filtered to upwind by Performance.tsx
                    cloud_data = (cloudToUse && Array.isArray(cloudToUse) && cloudToUse.length > 0) ? cloudToUse : [];
                    target_data = props.targets?.['UPWIND'] || props.targets || [];
                } else if (props.uwDw === 'DOWNWIND') {
                    aggregate_data = filterData(aggregatesToUse, "DW", "BOTH");
                    // Use cloud directly - it's already filtered to downwind by Performance.tsx
                    cloud_data = (cloudToUse && Array.isArray(cloudToUse) && cloudToUse.length > 0) ? cloudToUse : [];
                    target_data = props.targets?.['DOWNWIND'] || props.targets || [];
                } else {
                    aggregate_data = filterData(aggregatesToUse, "NONE", "BOTH");
                    // Use cloud directly - it's already filtered by Performance.tsx
                    cloud_data = (cloudToUse && Array.isArray(cloudToUse) && cloudToUse.length > 0) ? cloudToUse : [];
                    target_data = props.targets || [];
                }
            }
        } else {
            // Separate aggregate types provided - set target_data but skip aggregate_data filtering (will be done below)
            if (props.filters && props.filters.length > 0) {
                props.filters.forEach((filter: string) => {
                    if (filter == 'upwind') {
                        target_data = props.targets?.['UPWIND'] || [];
                    } else if (filter == 'downwind') {
                        target_data = props.targets?.['DOWNWIND'] || [];
                    }
                });
            } else {
                if (props.uwDw === 'UPWIND') {
                    target_data = props.targets?.['UPWIND'] || props.targets || [];
                } else if (props.uwDw === 'DOWNWIND') {
                    target_data = props.targets?.['DOWNWIND'] || props.targets || [];
                } else {
                    target_data = props.targets || [];
                }
            }
            // Cloud data is already filtered by Performance.tsx
            cloud_data = (cloudToUse && Array.isArray(cloudToUse) && cloudToUse.length > 0) ? cloudToUse : [];
        }

        // Target data structure: object with field names as keys (e.g., "m3_APW_HSB_LARW.kph_target": [...])
        // Only use targets if explicitly specified in chart configuration (taxis prop must be provided)
        let target_data_array: any[] = [];
        // Skip target lookup if taxis is empty (no target specified in chart configuration)
        if (!props.taxis || props.taxis.trim() === '') {
            // No target specified, skip target lookup
        } else if (target_data && typeof target_data === 'object' && !Array.isArray(target_data)) {
            // Target data is an object, use taxis prop directly as the key (it comes from chart configuration)
            const taxisField = props.taxis;
            const availableKeys = Object.keys(target_data);
            
            logDebug('AdvancedScatter: Looking for target data field', {
                taxisField,
                availableKeys,
                targetDataType: typeof target_data,
                isArray: Array.isArray(target_data)
            });
            
            // Try exact match first (chart configuration specifies the exact field name)
            if (target_data[taxisField] && Array.isArray(target_data[taxisField])) {
                target_data_array = target_data[taxisField];
                logDebug('AdvancedScatter: Found target data with exact match', { key: taxisField, count: target_data_array.length });
            } else {
                // Try case-insensitive match (in case of case differences)
                const matchingKey = availableKeys.find(key => key.toLowerCase() === taxisField.toLowerCase());
                if (matchingKey && Array.isArray(target_data[matchingKey])) {
                    target_data_array = target_data[matchingKey];
                    logDebug('AdvancedScatter: Found target data with case-insensitive match', { key: matchingKey, searched: taxisField, count: target_data_array.length });
                } else {
                    logError('AdvancedScatter: Could not find target data field', {
                        taxisField,
                        availableKeys,
                        firstFewKeys: availableKeys.slice(0, 10),
                        firstFewValues: availableKeys.slice(0, 3).map(k => ({ key: k, isArray: Array.isArray(target_data[k]), length: Array.isArray(target_data[k]) ? target_data[k].length : 'N/A' }))
                    });
                }
            }
        } else if (Array.isArray(target_data)) {
            // Target data is already an array
            target_data_array = target_data;
            logDebug('AdvancedScatter: Target data is already an array', { count: target_data_array.length });
        } else if (target_data) {
            logError('AdvancedScatter: Target data is not an object or array', {
                targetDataType: typeof target_data,
                isArray: Array.isArray(target_data),
                value: target_data
            });
        }

        // Update the map of Twa_deg_avg from AVG aggregates for filtering STD/AAV aggregates
        // This must be updated before filtering so getTwa can use it
        avgTwaDegAvgMap.clear();
        if (props.aggregatesAVG && Array.isArray(props.aggregatesAVG)) {
            props.aggregatesAVG.forEach((d: any) => {
                const eventId = d.event_id;
                if (eventId !== undefined && eventId !== null) {
                    const twaDegAvg = d.Twa_deg_avg ?? d.twa_deg_avg;
                    if (twaDegAvg !== undefined && twaDegAvg !== null && !isNaN(Number(twaDegAvg))) {
                        avgTwaDegAvgMap.set(eventId, Number(twaDegAvg));
                    }
                }
            });
        }

        // Use separate aggregate data for x-axis and y-axis if available
        const xType = (props.xType || props.aggregate || 'AVG').toUpperCase();
        const yType = (props.yType || props.aggregate || 'AVG').toUpperCase();
        
        // Filter separate aggregate types directly if available, otherwise use filtered aggregate_data
        let xFilteredData = aggregate_data;
        let yFilteredData = aggregate_data;
        let aggregate_data_r: any[] = [];

        if (props.aggregatesAVG || props.aggregatesSTD || props.aggregatesAAV) {
            // We have separate aggregate types - filter each one directly
            const aggregatesByType: { [key: string]: any[] } = {
                AVG: props.aggregatesAVG || [],
                STD: props.aggregatesSTD || [],
                AAV: props.aggregatesAAV || []
            };
            
            // Determine filter direction
            let filterDirection = "NONE";
            if (props.filters && props.filters.length > 0) {
                props.filters.forEach((filter: string) => {
                    if (filter == 'upwind') filterDirection = "UW";
                    else if (filter == 'downwind') filterDirection = "DW";
                });
            } else {
                if (props.uwDw === 'UPWIND') filterDirection = "UW";
                else if (props.uwDw === 'DOWNWIND') filterDirection = "DW";
            }
            
            // Filter each aggregate type separately
            const filteredAggregatesByType: { [key: string]: any[] } = {
                AVG: aggregatesByType.AVG.length > 0 ? filterData(aggregatesByType.AVG, filterDirection, "BOTH") : [],
                STD: aggregatesByType.STD.length > 0 ? filterData(aggregatesByType.STD, filterDirection, "BOTH") : [],
                AAV: aggregatesByType.AAV.length > 0 ? filterData(aggregatesByType.AAV, filterDirection, "BOTH") : []
            };
            
            // Debug logging for downwind filtering
            if (filterDirection === "DW" && (xType !== 'AVG' || yType !== 'AVG')) {
                // Test getTwa on a sample STD/AAV point to see what value it returns
                const sampleSTD = aggregatesByType.STD.length > 0 ? aggregatesByType.STD[0] : null;
                const sampleAAV = aggregatesByType.AAV.length > 0 ? aggregatesByType.AAV[0] : null;
                const testTwaSTD = sampleSTD ? getTwa(sampleSTD) : null;
                const testTwaAAV = sampleAAV ? getTwa(sampleAAV) : null;
                
                logDebug('[AdvancedScatter] Downwind filtering debug:', {
                    xType,
                    yType,
                    filterDirection,
                    beforeFilter: {
                        AVG: aggregatesByType.AVG.length,
                        STD: aggregatesByType.STD.length,
                        AAV: aggregatesByType.AAV.length
                    },
                    afterFilter: {
                        AVG: filteredAggregatesByType.AVG.length,
                        STD: filteredAggregatesByType.STD.length,
                        AAV: filteredAggregatesByType.AAV.length
                    },
                    sampleSTD: sampleSTD ? {
                        event_id: sampleSTD.event_id,
                        Twa_deg_avg: sampleSTD.Twa_deg_avg ?? sampleSTD.twa_deg_avg,
                        Twa_deg: sampleSTD.Twa_deg ?? sampleSTD.twa_deg,
                        getTwa_result: testTwaSTD,
                        isDownwind: testTwaSTD !== null ? Math.abs(testTwaSTD) > 90 : false,
                        allTwaKeys: Object.keys(sampleSTD).filter(k => k.toLowerCase().includes('twa'))
                    } : null,
                    sampleAAV: sampleAAV ? {
                        event_id: sampleAAV.event_id,
                        Twa_deg_avg: sampleAAV.Twa_deg_avg ?? sampleAAV.twa_deg_avg,
                        Twa_deg: sampleAAV.Twa_deg ?? sampleAAV.twa_deg,
                        getTwa_result: testTwaAAV,
                        isDownwind: testTwaAAV !== null ? Math.abs(testTwaAAV) > 90 : false,
                        allTwaKeys: Object.keys(sampleAAV).filter(k => k.toLowerCase().includes('twa'))
                    } : null
                });
            }
            
            // Use filtered data for x and y axes based on their types
            xFilteredData = filteredAggregatesByType[xType] || [];
            yFilteredData = filteredAggregatesByType[yType] || [];
            
            // When using separate aggregate types, pass empty array as collection since we're using xFilteredData/yFilteredData
            aggregate_data_r = reduceData([], props.xaxis, props.yaxis, xFilteredData, yFilteredData);
            
            // Debug logging for reduceData result
            if (filterDirection === "DW" && (xType !== 'AVG' || yType !== 'AVG')) {
                logDebug('[AdvancedScatter] After reduceData:', {
                    xType,
                    yType,
                    xFilteredDataLength: xFilteredData.length,
                    yFilteredDataLength: yFilteredData.length,
                    reducedDataLength: aggregate_data_r.length,
                    xaxis: props.xaxis,
                    yaxis: props.yaxis
                });
            }
        } else {
            // No separate aggregate types - use the standard flow
            aggregate_data_r = reduceData(aggregate_data, props.xaxis, props.yaxis, xFilteredData, yFilteredData);
        }
        
        // Create cloud_data_r from ONLY cloud data (props.cloud) - this contains only 1Hz point data, NOT aggregates
        // cloud_data comes from props.cloud which is already filtered 1Hz data from Performance.tsx
        const cloud_data_r = cloud_data && cloud_data.length > 0 ? reduceData(cloud_data, props.xaxis, props.yaxis) : [];
        
        // Debug: Log cloud data after reduceData
        if (cloud_data && cloud_data.length > 0) {
            logDebug('AdvancedScatter: Cloud data after reduceData', {
                inputCount: cloud_data.length,
                outputCount: cloud_data_r.length,
                xaxis: props.xaxis,
                yaxis: props.yaxis,
                reductionRatio: cloud_data.length > 0 ? (cloud_data_r.length / cloud_data.length).toFixed(2) : 'N/A'
            });
        }
        // Only process targets if taxis is specified (target explicitly configured in chart)
        let target_data_r: any[] = [];
        if (props.taxis && props.taxis.trim() !== '' && target_data_array.length > 0) {
            target_data_r = reduceTargets(target_data_array, props.xaxis.toLowerCase(), props.taxis);
        } else {
            // No target specified, skip target processing
        }

        // Debug logging for data processing
        if (aggregate_data_r.length === 0 && aggregate_data.length > 0) {
            logDebug('AdvancedScatter: reduceData returned empty array', {
                inputCount: aggregate_data.length,
                xaxis: props.xaxis,
                yaxis: props.yaxis,
                firstItem: aggregate_data[0] ? Object.keys(aggregate_data[0]) : []
            });
        }

        // Use cloud data for bounds if available, otherwise use aggregate data
        if (cloud_data_r && cloud_data_r.length > 0) {
            getYBounds([cloud_data_r]);
            getXBounds([cloud_data_r], cloud_data_r, aggregate_data_r);
        } else if (aggregate_data_r && aggregate_data_r.length > 0) {
            getXBounds([aggregate_data_r], cloud_data_r, aggregate_data_r);
            getYBounds([aggregate_data_r]);
        } else {
            // No data to draw - log warning
            logDebug('AdvancedScatter: No data to draw', {
                aggregateInput: aggregatesToUse?.length || 0,
                aggregateFiltered: aggregate_data.length,
                aggregateReduced: aggregate_data_r.length,
                cloudInput: cloudToUse?.length || 0,
                cloudFiltered: cloud_data.length,
                cloudReduced: cloud_data_r.length
            });
            return; // Exit early if no data
        }
        
        drawAxes();

        // ============================================================================
        // CLOUD FUNCTIONALITY - TEMPORARILY DISABLED
        // ============================================================================
        // TO RESTORE CLOUD VISUALIZATION:
        // 1. Uncomment the code block below
        // 2. Remove or comment out the removeCloudArea() call
        // 3. Ensure cloud data fetching is enabled in Performance.tsx (see notes there)
        // 4. Ensure cloud buttons are visible in PerfSettings.tsx (see notes there)
        //
        // NOTES:
        // - Cloud visualization uses ONLY cloud_data_r (1Hz data from props.cloud)
        // - Cloud data should NOT include aggregates (1 point per event_id)
        // - Bandwidth settings: normal=15, zoom=25 (adjust for contour smoothness)
        // - Thresholds: 20 (adjust for number of contour levels)
        // - Opacity: min=0.25, max=0.6 (in drawArea function)
        // - Fill colors: dark mode (#9ca3af to lightgrey), light mode (#f3f4f6 to #6b7280)
        // ============================================================================
        // Draw cloud area first (background layer) - uses ONLY cloud_data_r (1Hz data, no aggregates)
        // if (cloud_data_r && cloud_data_r.length > 0) {
        //     drawArea(cloud_data_r);
        // } else {
        //     // Explicitly remove cloud if no cloud data
        //     removeCloudArea();
        // }
        // Always remove cloud for now
        removeCloudArea();
        refreshRecencyScaleFromData(aggregate_data_r);
        // Draw scatter points (middle layer)
        drawScatter(aggregate_data_r);
        // Draw target line last (top layer - after cloud and scatter points) - only if target is specified
        if (target_data_r && target_data_r.length > 0) {
            drawSpline(target_data_r);
        }
        // Baselines sit above the target, so a reference comparison is never hidden behind it.
        const drawnBaselines = drawBaselineSplines();
        setReferenceCurves({ target: target_data_r ?? [], baselines: drawnBaselines });

        // Full row set for fits / filtered(); drawScatter may plot fewer for performance (density-aware cap).
        setFiltered(aggregate_data_r);

        if (fitsEnabled()) {
            scheduleDrawFit();
        }

        if (isHighDensityMode) {
            // Canvas mode: selection dimming is baked into the canvas paint; keep the hit
            // rect / lasso above the target + grid overlays drawn after drawScatter.
            renderPointsCanvas(canvasScatterData);
            raiseCanvasScatterHitOverlay();
        } else {
            updatePointSizesAndOpacity();
        }
    };

    // Partial redraw for visibility changes (from FleetScatter)
    function drawDataElementsOnly(capturedAggregates?: any[], capturedCloud?: any[]): void {
        if (!chartRef || !chartRef.isConnected) return;
        const svg = d3.select(chartRef).select("svg");
        if (!svg.node()) {
            drawChart(capturedAggregates, capturedCloud);
            return;
        }
        const xAxisSelection = svg.select(".x-axis");
        const yAxisSelection = svg.select(".y-axis");
        if (xAxisSelection.empty() || yAxisSelection.empty()) {
            drawChart(capturedAggregates, capturedCloud);
            return;
        }

        const aggregatesToUse = capturedAggregates !== undefined ? capturedAggregates : (lastCapturedAggregates !== undefined ? lastCapturedAggregates : []);
        // Always use current props.cloud if capturedCloud is not provided - never use stale lastCapturedCloud
        // This ensures we always have the correct filtered cloud data (upwind or downwind) from Performance.tsx
        const cloudToUse = capturedCloud !== undefined ? capturedCloud : (props.cloud && Array.isArray(props.cloud) ? props.cloud : []);

        let aggregate_data: any[] = [];
        let cloud_data: any[] = [];
        let target_data: any = [];

        if (props.filters && props.filters.length > 0) {
            props.filters.forEach((filter: string) => {
                if (filter == 'upwind') {
                    aggregate_data = filterData(aggregatesToUse, "UW", "BOTH");
                    // props.cloud is already filtered by Performance.tsx, so use it directly without re-filtering
                    cloud_data = (cloudToUse && Array.isArray(cloudToUse) && cloudToUse.length > 0) ? cloudToUse : [];
                    target_data = props.targets?.['UPWIND'] || [];
                } else if (filter == 'downwind') {
                    aggregate_data = filterData(aggregatesToUse, "DW", "BOTH");
                    // props.cloud is already filtered by Performance.tsx, so use it directly without re-filtering
                    cloud_data = (cloudToUse && Array.isArray(cloudToUse) && cloudToUse.length > 0) ? cloudToUse : [];
                    target_data = props.targets?.['DOWNWIND'] || [];
                }
            });
        } else {
            // When filters are empty, check uwDw prop for aggregate filtering
            // NOTE: props.cloud is already filtered by Performance.tsx based on UwDw toggle,
            // so we should NOT filter it again here - just use it directly
            if (props.uwDw === 'UPWIND') {
                aggregate_data = filterData(aggregatesToUse, "UW", "BOTH");
                // Use cloud directly - it's already filtered to upwind by Performance.tsx
                cloud_data = (cloudToUse && Array.isArray(cloudToUse) && cloudToUse.length > 0) ? cloudToUse : [];
                target_data = props.targets?.['UPWIND'] || props.targets || [];
            } else if (props.uwDw === 'DOWNWIND') {
                aggregate_data = filterData(aggregatesToUse, "DW", "BOTH");
                // Use cloud directly - it's already filtered to downwind by Performance.tsx
                cloud_data = (cloudToUse && Array.isArray(cloudToUse) && cloudToUse.length > 0) ? cloudToUse : [];
                target_data = props.targets?.['DOWNWIND'] || props.targets || [];
            } else {
                aggregate_data = filterData(aggregatesToUse, "NONE", "BOTH");
                // Use cloud directly - it's already filtered by Performance.tsx
                cloud_data = (cloudToUse && Array.isArray(cloudToUse) && cloudToUse.length > 0) ? cloudToUse : [];
                target_data = props.targets || [];
            }
        }

        // Target data structure: object with field names as keys (e.g., "m3_APW_HSB_LARW.kph_target": [...])
        // Use the exact field name from chart configuration (taxis prop) to look up the target data
        // The chart configuration specifies the exact field name in yaxisTarget.dataField or yaxisTarget.name
        let target_data_array: any[] = [];
        if (target_data && typeof target_data === 'object' && !Array.isArray(target_data)) {
            // Target data is an object, use taxis prop directly as the key (it comes from chart configuration)
            const taxisField = props.taxis;
            const availableKeys = Object.keys(target_data);
            
            logDebug('AdvancedScatter: Looking for target data field', {
                taxisField,
                availableKeys,
                targetDataType: typeof target_data,
                isArray: Array.isArray(target_data)
            });
            
            // Try exact match first (chart configuration specifies the exact field name)
            if (target_data[taxisField] && Array.isArray(target_data[taxisField])) {
                target_data_array = target_data[taxisField];
                logDebug('AdvancedScatter: Found target data with exact match', { key: taxisField, count: target_data_array.length });
            } else {
                // Try case-insensitive match (in case of case differences)
                const matchingKey = availableKeys.find(key => key.toLowerCase() === taxisField.toLowerCase());
                if (matchingKey && Array.isArray(target_data[matchingKey])) {
                    target_data_array = target_data[matchingKey];
                    logDebug('AdvancedScatter: Found target data with case-insensitive match', { key: matchingKey, searched: taxisField, count: target_data_array.length });
                } else {
                    logError('AdvancedScatter: Could not find target data field', {
                        taxisField,
                        availableKeys,
                        firstFewKeys: availableKeys.slice(0, 10),
                        firstFewValues: availableKeys.slice(0, 3).map(k => ({ key: k, isArray: Array.isArray(target_data[k]), length: Array.isArray(target_data[k]) ? target_data[k].length : 'N/A' }))
                    });
                }
            }
        } else if (Array.isArray(target_data)) {
            // Target data is already an array
            target_data_array = target_data;
            logDebug('AdvancedScatter: Target data is already an array', { count: target_data_array.length });
        } else {
            logError('AdvancedScatter: Target data is not an object or array', {
                targetDataType: typeof target_data,
                isArray: Array.isArray(target_data),
                value: target_data
            });
        }

        // Use separate aggregate data for x-axis and y-axis if available
        const xType = (props.xType || props.aggregate || 'AVG').toUpperCase();
        const yType = (props.yType || props.aggregate || 'AVG').toUpperCase();
        const aggregatesByType: { [key: string]: any[] } = {
            AVG: props.aggregatesAVG || aggregate_data,
            STD: props.aggregatesSTD || aggregate_data,
            AAV: props.aggregatesAAV || aggregate_data
        };
        const xAggregateData = aggregatesByType[xType] || aggregate_data;
        const yAggregateData = aggregatesByType[yType] || aggregate_data;
        
        // Filter x and y data separately when we have separate aggregate data
        // This is needed even when xType === yType because the x-axis channel might be different
        let xFilteredData = aggregate_data;
        let yFilteredData = aggregate_data;
        if (props.aggregatesAVG || props.aggregatesSTD || props.aggregatesAAV) {
            // Apply same filters to x and y data using their respective aggregate types
            if (props.filters && props.filters.length > 0) {
                props.filters.forEach((filter: string) => {
                    if (filter == 'upwind') {
                        xFilteredData = filterData(xAggregateData, "UW", "BOTH");
                        yFilteredData = filterData(yAggregateData, "UW", "BOTH");
                    } else if (filter == 'downwind') {
                        xFilteredData = filterData(xAggregateData, "DW", "BOTH");
                        yFilteredData = filterData(yAggregateData, "DW", "BOTH");
                    }
                });
            } else {
                if (props.uwDw === 'UPWIND') {
                    xFilteredData = filterData(xAggregateData, "UW", "BOTH");
                    yFilteredData = filterData(yAggregateData, "UW", "BOTH");
                } else if (props.uwDw === 'DOWNWIND') {
                    xFilteredData = filterData(xAggregateData, "DW", "BOTH");
                    yFilteredData = filterData(yAggregateData, "DW", "BOTH");
                } else {
                    xFilteredData = filterData(xAggregateData, "NONE", "BOTH");
                    yFilteredData = filterData(yAggregateData, "NONE", "BOTH");
                }
            }
        }
        
        const aggregate_data_r = reduceData(aggregate_data, props.xaxis, props.yaxis, xFilteredData, yFilteredData);
        // Always reduce cloud_data, even if empty, to ensure we get an empty array (not undefined)
        const cloud_data_r = (cloud_data && Array.isArray(cloud_data) && cloud_data.length > 0) ? reduceData(cloud_data, props.xaxis, props.yaxis) : [];
        // Only process targets if taxis is specified (target explicitly configured in chart)
        let target_data_r: any[] = [];
        if (props.taxis && props.taxis.trim() !== '' && target_data_array.length > 0) {
            target_data_r = reduceTargets(target_data_array, props.xaxis.toLowerCase(), props.taxis);
        } else {
            logDebug('AdvancedScatter [drawDataElementsOnly]: Skipping target processing - no target specified in chart configuration', {
                taxis: props.taxis,
                targetDataArrayLength: target_data_array.length
            });
        }

        if (aggregate_data_r && aggregate_data_r.length > 0) {
            getXBounds([aggregate_data_r], cloud_data_r, aggregate_data_r);
            getYBounds([aggregate_data_r]);
        }

        const containerWidth = chartRef?.clientWidth || 450;
        const containerHeight = chartRef?.clientHeight || 500;
        chartWidth = containerWidth - margin.left - margin.right;
        chartHeight = containerHeight - margin.top - margin.bottom;

        // Check if x-axis is the default (TWA) to ensure we use the calculated bounds
        const twaChannel = twaName().toLowerCase();
        const xaxisLower = props.xaxis ? props.xaxis.toLowerCase() : '';
        const isDefaultXAxis = xaxisLower === twaChannel || xaxisLower === 'twa';
        
        let xDomainMin = xMin;
        let xDomainMax = xMax;
        if (xMin === 9999999 || xMax === -9999999 || xMin > xMax) {
            xDomainMin = 5;
            xDomainMax = 25;
        }
        
        // For default x-axis, ensure we're using the calculated bounds (which include interval padding)
        if (isDefaultXAxis && xMin !== 9999999 && xMax !== -9999999) {
            xDomainMin = xMin;
            xDomainMax = xMax;
        }
        
        xScale = d3.scaleLinear().range([0, chartWidth]).domain([xDomainMin, xDomainMax]);
        yScale = d3.scaleLinear().range([chartHeight, 0]).domain([yMin, yMax]);

        const chartbody = svg.select("g");
        chartbody.selectAll(".scatter").remove();
        chartbody.selectAll(".target-line, .target-path, .tgt, .fit, .bl").remove();
        // Always remove cloud first
        chartbody.selectAll(".cloud-area").remove();
        svg.selectAll(".lasso").remove();

        isLassoActive = false;
        listenersAttached = false;

        // Draw cloud area only if we have data
        if (cloud_data_r && cloud_data_r.length > 0) {
            drawArea(cloud_data_r);
        } else {
            // Explicitly ensure cloud is removed
            removeCloudArea();
        }
        refreshRecencyScaleFromData(aggregate_data_r);
        // Draw scatter points (middle layer)
        drawScatter(aggregate_data_r);
        // Draw target line last (top layer - after cloud and scatter points) - only if target is specified
        if (target_data_r && target_data_r.length > 0) {
            drawSpline(target_data_r);
        }
        // Baselines sit above the target, so a reference comparison is never hidden behind it.
        const drawnBaselines = drawBaselineSplines();
        setReferenceCurves({ target: target_data_r ?? [], baselines: drawnBaselines });

        // Full row set for fits / filtered(); drawScatter may plot fewer for performance (density-aware cap).
        setFiltered(aggregate_data_r);

        if (fitsEnabled()) {
            scheduleDrawFit();
        }

        if (isHighDensityMode) {
            // Canvas mode: selection dimming is baked into the canvas paint; keep the hit
            // rect / lasso above the target + grid overlays drawn after drawScatter.
            renderPointsCanvas(canvasScatterData);
            raiseCanvasScatterHitOverlay();
        } else {
            updatePointSizesAndOpacity();
        }
    }

    // Update colors function with signature-based change detection
    function updateColors(): void {
        if (!chartRef) return;
        const svg = d3.select(chartRef).select("svg");
        if (!svg.node()) return;

        if (isHighDensityMode) {
            // Canvas mode: repaint with the new colors (no SVG transition).
            renderPointsCanvas(canvasScatterData);
            return;
        }

        const getFillColorLocal = (d: any) => getColor(d);
        const strokeColor = getThemeStrokeColor();

        svg.selectAll("circle.scatter")
            .transition()
            .duration(500)
            .style("fill", (d: any) => getFillColorLocal(d))
            .style("stroke", strokeColor);

        svg.selectAll("rect.scatter")
            .transition()
            .duration(500)
            .style("fill", (d: any) => getFillColorLocal(d))
            .style("stroke", strokeColor);
    }

    // Update point sizes and opacity based on selection
    const updatePointSizesAndOpacity = () => {
        if (!chartRef) return;
        const svg = d3.select(chartRef).select("svg").node();
        if (!svg) return;

        if (isHighDensityMode) {
            // Canvas mode: no hover-grow; repaint applies selection sizing + dimming.
            renderPointsCanvas(canvasScatterData);
            return;
        }

        const selected = domainSelectedEvents();
        const hasSelection = selected.length > 0;
        const scatterPoints = d3.select(chartRef).selectAll(".scatter");
        const currentMouseID = typeof props.mouseID === 'function' ? props.mouseID() : (props.mouseID || null);

        if (scatterPoints.size() > 0) {
            scatterPoints.filter("circle")
                .transition()
                .duration(200)
                .attr("r", (d: any) => {
                    if (!d || d.ID === undefined || d.ID === null) return 5;
                    const isSelected = hasSelection && selected.includes(d.ID);
                    const isHovered = currentMouseID && d.ID === currentMouseID;
                    return scatterPointGeometry(d, isSelected, hasSelection, !!isHovered).r;
                });

            scatterPoints.filter("rect")
                .transition()
                .duration(200)
                .attr("width", (d: any) => {
                    if (!d || d.ID === undefined || d.ID === null) return 8.25;
                    const isSelected = hasSelection && selected.includes(d.ID);
                    const isHovered = currentMouseID && d.ID === currentMouseID;
                    return scatterPointGeometry(d, isSelected, hasSelection, !!isHovered).rectSize;
                })
                .attr("height", (d: any) => {
                    if (!d || d.ID === undefined || d.ID === null) return 8.25;
                    const isSelected = hasSelection && selected.includes(d.ID);
                    const isHovered = currentMouseID && d.ID === currentMouseID;
                    return scatterPointGeometry(d, isSelected, hasSelection, !!isHovered).rectSize;
                });

            scatterPoints.each(function (d: any) {
                if (d.ID === undefined || d.ID === null) return;
                d3.select(this).style('opacity', getScatterPointOpacity(d, selected));
            });
        }
    };

    // Effect to watch for initial aggregates data and trigger draw
    let lastAggregatesLength = 0;
    let initialDrawTimer: ReturnType<typeof setTimeout> | null = null;
    createEffect(() => {
        if (isCleaningUp) return; // Don't run during cleanup
        if (!chartRef || !chartRef.isConnected) return;
        
        const aggregatesValue = props.aggregates;
        const cloudValue = props.cloud;
        
        // Check if aggregates changed from empty to having data
        const currentLength = Array.isArray(aggregatesValue) ? aggregatesValue.length : 0;
        const aggregatesChanged = currentLength !== lastAggregatesLength;
        lastAggregatesLength = currentLength;
        
        // Only draw if we have data and (chart doesn't exist or data changed)
        if (aggregatesValue && Array.isArray(aggregatesValue) && aggregatesValue.length > 0) {
            const chartExists = d3.select(chartRef).select("svg").node();
            if (!chartExists || aggregatesChanged) {
                // Clear any pending draw
                if (initialDrawTimer) {
                    clearTimeout(initialDrawTimer);
                }
                
                // Use a small delay to ensure DOM is ready
                initialDrawTimer = setTimeout(() => {
                    // Check cleanup flag and connection before proceeding
                    if (isCleaningUp || !chartRef || !chartRef.isConnected) {
                        initialDrawTimer = null;
                        return;
                    }
                    const isChartVisible = isVisible();
                    const rect = chartRef.getBoundingClientRect();
                    const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;
                    
                    // Draw only when near the viewport; IntersectionObserver (rootMargin) handles preload.
                    // Avoid `|| !chartExists` — it forced every chart on the page to draw at once and locked the UI.
                    if (isChartVisible || isInViewport) {
                        drawChart(aggregatesValue, cloudValue);
                        requestAnimationFrame(() => props.onChartRendered?.());
                    }
                    initialDrawTimer = null;
                }, 100);
            }
        }
    });


    // Unified filter effect with detailed signature matching (fixes duplicate effects issue)
    let filterRedrawTimer: ReturnType<typeof setTimeout> | null = null;
    let lastFilterSignature = '';
    let filterEffectCount = 0;
    let lastUpdateCharts: boolean | undefined = undefined;
    let lastUwDw: string | undefined = undefined;
    let lastTargetName: string | undefined = undefined;
    let lastTargetColor: string | undefined = undefined;
    let lastBaselineSignature: string | undefined = undefined;

    createEffect(() => {
        if (isCleaningUp) return; // Don't run during cleanup
        if (!isVisible()) return;
        if (isUpdatingData) return;
        if (!chartRef || !chartRef.isConnected) return;

        filterEffectCount++;
        if (filterEffectCount > 50) {
            logError('🚨 INFINITE LOOP DETECTED in AdvancedScatter filter effect!', filterEffectCount);
            filterEffectCount = 0;
            return;
        }

        // Track all filter signals with detailed signature (from PerfScatter)
        const filterState = {
            states: selectedStatesTimeseries(),
            races: selectedRacesTimeseries(),
            legs: selectedLegsTimeseries(),
            grades: selectedGradesTimeseries()
        };

        const statesStr = filterState.states.sort().join(',');
        const racesStr = filterState.races.sort().join(',');
        const legsStr = filterState.legs.sort().join(',');
        const gradesStr = filterState.grades.sort().join(',');
        const currentFilterSignature = `${statesStr}|${racesStr}|${legsStr}|${gradesStr}`;

        // Also track updateCharts, uwDw, and target selection changes
        const currentUpdateCharts = props.updateCharts;
        const currentUwDw = props.uwDw;
        const currentTargetName = props.targetName ?? '';
        const currentTargetColor = props.targetColor ?? '#00ff7f';
        // One entry per slot, so emptying or swapping either baseline repaints. The revision is
        // what distinguishes two different baselines that happen to share a name and colour;
        // name/config/colour are included so a config or recolour also repaints. Slot count is part
        // of the signature by construction — dropping a slot shortens the string.
        const currentBaselineSignature = (props.baselines ?? [])
            .map((o) => [o?.name ?? '', o?.config ?? '', o?.color ?? '', o?.revision ?? 0].join('|'))
            .join('#');
        const dataChanged = currentUpdateCharts !== lastUpdateCharts
            || currentUwDw !== lastUwDw
            || currentFilterSignature !== lastFilterSignature
            || currentTargetName !== lastTargetName
            || currentTargetColor !== lastTargetColor
            || currentBaselineSignature !== lastBaselineSignature;

        if (!dataChanged) {
            filterEffectCount = 0;
            return;
        }

        const filterSignatureChanged = currentFilterSignature !== lastFilterSignature;
        const uwDwChanged = currentUwDw !== lastUwDw;
        const targetChanged = currentTargetName !== lastTargetName
            || currentTargetColor !== lastTargetColor;
        const baselineChanged = currentBaselineSignature !== lastBaselineSignature;
        const updateChartsChanged = currentUpdateCharts !== lastUpdateCharts;
        const onlyUpdateChartsEnabled = updateChartsChanged
            && currentUpdateCharts === true
            && lastUpdateCharts === false
            && !filterSignatureChanged
            && !uwDwChanged
            && !targetChanged
            && !baselineChanged;

        if (onlyUpdateChartsEnabled) {
            const existingChart = d3.select(chartRef).select("svg").node();
            if (existingChart) {
                // Initial-draw effect already painted; skip redundant full redraw storm.
                lastUpdateCharts = currentUpdateCharts;
                filterEffectCount = 0;
                return;
            }
        }

        lastFilterSignature = currentFilterSignature;
        lastUpdateCharts = currentUpdateCharts;
        lastUwDw = currentUwDw;
        lastTargetName = currentTargetName;
        lastTargetColor = currentTargetColor;
        lastBaselineSignature = currentBaselineSignature;

        if (filterRedrawTimer) {
            clearTimeout(filterRedrawTimer);
        }

        filterRedrawTimer = setTimeout(() => {
            // Check cleanup flag and connection before proceeding
            if (isCleaningUp || !chartRef || !chartRef.isConnected) {
                filterRedrawTimer = null;
                return;
            }

            const aggregatesValue = untrack(() => props.aggregates);
            const cloudValue = untrack(() => props.cloud);
            const chartNode = untrack(() => d3.select(chartRef).select("svg").node());
            
            requestAnimationFrame(() => {
                // Double-check before executing (component might have been unmounted)
                if (isCleaningUp || !chartRef || !chartRef.isConnected) {
                    filterEffectCount = 0;
                    filterRedrawTimer = null;
                    return;
                }
                if (aggregatesValue && chartNode) {
                    drawChart(aggregatesValue, cloudValue);
                    // Notify parent so mode-switching overlay can be hidden
                    requestAnimationFrame(() => props.onChartRendered?.());
                }
                filterEffectCount = 0;
            });
            
            filterRedrawTimer = null;
        }, 200);
    });

    // Combined zoom/selection/highlights effect with debouncing to prevent lockups
    let zoomFitTimer: ReturnType<typeof setTimeout> | null = null;
    let zoomFitRafId: number | null = null;
    let lastZoomState: boolean | undefined = undefined;
    let lastSelectionSignature = '';
    let lastHighlightsSignature = '';

    function cancelZoomFitWork(): void {
        if (zoomFitTimer != null) {
            clearTimeout(zoomFitTimer);
            zoomFitTimer = null;
        }
        if (zoomFitRafId != null) {
            cancelAnimationFrame(zoomFitRafId);
            zoomFitRafId = null;
        }
    }
    
    createEffect(() => {
        if (isCleaningUp) return; // Don't run effects during cleanup
        if (!isVisible()) return;
        if (!chartRef || !chartRef.isConnected) return;
        if (isUpdatingData) return;
        
        // Fits on/off follows zoom and the Display Options (see fitsEnabled), not zoom alone.
        const isZoomed = fitsEnabled();
        const selection = domainSelectedEvents();
        const highlights = props.highlights && Array.isArray(props.highlights) ? props.highlights : [];
        
        // Create signatures to detect actual changes
        const selectionSignature = Array.isArray(selection) ? selection.sort().join(',') : '';
        const highlightsSignature = Array.isArray(highlights) ? highlights.map((h: any) => String(h)).sort().join(',') : '';
        
        // Check if anything actually changed
        const zoomChanged = isZoomed !== lastZoomState;
        const selectionChanged = selectionSignature !== lastSelectionSignature;
        const highlightsChanged = highlightsSignature !== lastHighlightsSignature;
        
        if (!zoomChanged && !selectionChanged && !highlightsChanged) {
            return; // No changes, skip
        }
        
        lastZoomState = isZoomed;
        lastSelectionSignature = selectionSignature;
        lastHighlightsSignature = highlightsSignature;
        
        // Abort any pending zoom fit work so we don't run after exit or during cleanup
        cancelZoomFitWork();
        
        if (isZoomed && filtered().length > 0) {
            // Debounce the expensive drawFit operation
            zoomFitTimer = setTimeout(() => {
                if (isCleaningUp || !chartRef || !chartRef.isConnected) {
                    zoomFitTimer = null;
                    return;
                }
                zoomFitRafId = requestAnimationFrame(() => {
                    zoomFitRafId = null;
                    if (isCleaningUp || !chartRef || !chartRef.isConnected) {
                        zoomFitTimer = null;
                        return;
                    }
                    if (fitsEnabled() && filtered().length > 0) {
                        drawFit();
                    }
                    zoomFitTimer = null;
                });
            }, 100); // Small debounce to batch rapid changes
        } else if (!isZoomed) {
            // Clear fit lines when fits turn off (zooming out, or the Fits / Highlight toggles)
            fitDrawSeq++;
            if (!isCleaningUp && chartRef && chartRef.isConnected) {
                const chart = d3.select(chartRef).select("svg").select("g");
                if (chart.node()) {
                    chart.selectAll(".fit").remove();
                }
                setFitData([]);
            }
        }
    });

    // Unified color/groups/colorScale effect with signature-based change detection
    let lastColorGroupsSignature = '';
    let colorGroupsEffectCount = 0;
    /** DENSITY / VMG when the last color-by was one of them, else ''. */
    let lastPerDrawColor = '';

    createEffect(() => {
        if (!chartRef || !chartRef.isConnected) return;
        if (!isVisible() && !isScatterChartInViewport()) return;

        colorGroupsEffectCount++;
        if (colorGroupsEffectCount > 50) {
            logError('🚨 INFINITE LOOP DETECTED in AdvancedScatter color/groups effect!', colorGroupsEffectCount);
            colorGroupsEffectCount = 0;
            return;
        }

        if (props.color && props.groups && props.aggregates) {
            const groupsSignature = props.groups.map((g: any) => `${g.name}:${g.color}`).join('|');
            const aggregatesLength = Array.isArray(props.aggregates) ? props.aggregates.length : 0;
            const colorScaleExists = props.colorScale && typeof props.colorScale === 'function' ? '1' : '0';
            const currentSignature = `${props.color}|${groupsSignature}|${aggregatesLength}|${colorScaleExists}`;

            if (currentSignature === lastColorGroupsSignature) {
                colorGroupsEffectCount = 0;
                return;
            }

            lastColorGroupsSignature = currentSignature;
            const chartExists = d3.select(chartRef).select("svg").node();
            // DENSITY / VMG heat is computed per draw (and changes draw order), so switching into, out of
            // or between them needs a full redraw; any other color change only re-tints.
            const perDrawColor = isPerformanceRampColor(props.color) ? props.color : '';
            const perDrawColorChanged = perDrawColor !== lastPerDrawColor;
            lastPerDrawColor = perDrawColor;
            // Full drawChart per chart was tied to onLocalSelectionAssign and forced a redraw storm
            // on Performance (many scatters). Datum already carries `selection`; updateColors is enough
            // for color/group changes. Hotkey assign still triggers drawChart via parent's updateCharts toggle.
            if (chartExists && perDrawColorChanged) {
                setColorReloadCursor(true);
                try {
                    drawChart(props.aggregates, props.cloud);
                } finally {
                    scheduleClearColorReloadCursorAfterPaint();
                }
            } else if (chartExists) {
                setColorReloadCursor(true);
                updateColors();
                setTimeout(() => {
                    setColorReloadCursor(false);
                }, SCATTER_COLOR_TRANSITION_MS + SCATTER_COLOR_CURSOR_CLEAR_BUFFER_MS);
            } else if (isVisible()) {
                setColorReloadCursor(true);
                try {
                    drawChart(props.aggregates, props.cloud);
                } finally {
                    scheduleClearColorReloadCursorAfterPaint();
                }
            }
            colorGroupsEffectCount = 0;
        }
    });

    // Display Options → Highlight Fits & Targets. The SVG path follows the container class (CSS);
    // only the canvas has to repaint.
    createEffect(() => {
        performanceDisplayStore.highlightScatterLines();
        if (!chartRef || !chartRef.isConnected || isCleaningUp) {return;}
        if (isHighDensityMode && canvasScatterData.length > 0) {
            renderPointsCanvas(canvasScatterData);
        }
    });

    // Color scale availability effect - watch for colorScale changes (e.g., when sources become ready)
    createEffect(() => {
        if (!chartRef || !chartRef.isConnected) return;
        if (!isVisible()) return;
        
        const scale = props.colorScale;
        const color = props.color;
        
        // If we're coloring by SOURCE_NAME and colorScale becomes available, update colors
        if (color === 'SOURCE_NAME' && scale && typeof scale === 'function') {
            const chartExists = d3.select(chartRef).select("svg").node();
            if (chartExists) {
                // Use requestAnimationFrame to ensure DOM is ready
                requestAnimationFrame(() => {
                    setColorReloadCursor(true);
                    updateColors();
                    setTimeout(() => {
                        setColorReloadCursor(false);
                    }, SCATTER_COLOR_TRANSITION_MS + SCATTER_COLOR_CURSOR_CLEAR_BUFFER_MS);
                });
            }
        }
    });

    // X-axis change effect
    createEffect(() => {
        if (!chartRef || !chartRef.isConnected) return;
        if (!isVisible()) return;
        
        const currentXAxis = props.xaxis;
        if (currentXAxis && props.aggregates) {
            const chartExists = d3.select(chartRef).select("svg").node();
            if (chartExists) {
                d3.select(chartRef).selectAll("*").remove();
                drawChart();
            }
        }
    });

    // Theme change effect - update stroke colors when theme changes
    createEffect(() => {
        if (!chartRef || !chartRef.isConnected) return;
        if (!isVisible()) return;
        
        // Track theme changes to trigger effect
        void theme();
        
        // Update stroke colors when theme changes - use updateColors to ensure consistency
        const svg = d3.select(chartRef).select("svg");
        if (svg.node()) {
            updateColors();
        }
    });

    // Selection update effects (debounced)
    let triggerSelectionTimer: ReturnType<typeof setTimeout> | null = null;
    createEffect(() => {
        if (!chartRef || !chartRef.isConnected) return;
        if (!isVisible()) return;
        
        if (triggerSelection()) {
            if (triggerSelectionTimer) {
                clearTimeout(triggerSelectionTimer);
            }
            triggerSelectionTimer = setTimeout(() => {
                if (!chartRef) return;
                const chartExists = d3.select(chartRef).select("svg").node();
                if (chartExists) {
                    updatePointSizesAndOpacity();
                }
                triggerSelectionTimer = null;
            }, 50);
        }
    });

    let selectionUpdateTimer: ReturnType<typeof setTimeout> | null = null;
    createEffect(() => {
        if (!chartRef || !chartRef.isConnected) return;
        if (!isVisible()) return;
        
        domainSelectedEvents();
        if (selectionUpdateTimer) {
            clearTimeout(selectionUpdateTimer);
        }
        selectionUpdateTimer = setTimeout(() => {
            if (!chartRef) return;
            const chartExists = d3.select(chartRef).select("svg").node();
            if (chartExists) {
                // Update point sizes/opacity for selection changes, but don't touch cloud
                updatePointSizesAndOpacity();
            }
            selectionUpdateTimer = null;
        }, 50);
    });

    // React to the live recency layer toggling (e.g. pausing/disabling live mode) so the
    // recency emphasis (brighter/larger recent points) clears immediately.
    createEffect(() => {
        void props.liveRecencyOpacity;
        if (!chartRef || !chartRef.isConnected) return;
        if (!isVisible()) return;
        const chartExists = d3.select(chartRef).select("svg").node();
        if (chartExists) {
            updatePointSizesAndOpacity();
        }
    });

    // Separate effect for mouseID changes (hover) - only updates point sizes, never touches cloud
    createEffect(() => {
        if (!chartRef || !chartRef.isConnected) return;
        if (!isVisible()) return;
        
        // Access mouseID to track hover changes, but only update point sizes
        // This effect should NOT trigger any cloud redraws
        if (typeof props.mouseID === 'function') {
            props.mouseID();
        } else {
            void props.mouseID;
        }
        const chartExists = d3.select(chartRef).select("svg").node();
        if (chartExists) {
            // Only update point sizes for hover - never touch cloud
            updatePointSizesAndOpacity();
        }
    });

    // Visibility effect
    createEffect(() => {
        const currentVisibility = isVisible();
        const visibilityChanged = currentVisibility !== lastVisibilityState;
        lastVisibilityState = currentVisibility;
        
        if (currentVisibility && chartRef) {
            requestAnimationFrame(() => {
                if (!chartRef) return;
                const chartExists = d3.select(chartRef).select("svg").node();
                if (chartExists) {
                    // Only check cloud state when visibility actually changes - this prevents cloud from disappearing on hover
                    // Don't call updatePointSizesAndOpacity here - it reads props.mouseID which would make this effect
                    // re-run on every hover, causing unnecessary cloud checks
                    if (!visibilityChanged) {
                        return; // Don't check cloud on every effect run, only when visibility changes
                    }
                    
                    const now = Date.now();
                    const timeSinceLastCheck = now - lastVisibilityEffectTime;
                    if (timeSinceLastCheck < VISIBILITY_EFFECT_THROTTLE_MS) {
                        return; // Throttle even visibility changes
                    }
                    lastVisibilityEffectTime = now;
                    
                    // Only check cloud state if cloud data should exist
                    const shouldHaveCloud = props.cloud && Array.isArray(props.cloud) && props.cloud.length > 0;
                    if (shouldHaveCloud) {
                        // More robust cloud check - look in multiple locations
                        const svg = d3.select(chartRef).select("svg");
                        const chartbody = svg.select("g");
                        const hasCloudInSvg = svg.selectAll(".cloud-area").size() > 0;
                        const hasCloudInBody = chartbody.selectAll(".cloud-area").size() > 0;
                        const hasCloud = hasCloudInSvg || hasCloudInBody;
                        
                            if (!hasCloud) {
                                // Cloud state is incorrect - redraw data elements to fix it
                                // Use current props.cloud (already filtered) instead of lastCapturedCloud to ensure correct filtering
                                drawDataElementsOnly(lastCapturedAggregates, props.cloud);
                            }
                    } else {
                        // Cloud should not exist - ensure it's removed immediately
                        const svg = d3.select(chartRef).select("svg");
                        const chartbody = svg.select("g");
                        const hasCloudInSvg = svg.selectAll(".cloud-area").size() > 0;
                        const hasCloudInBody = chartbody.selectAll(".cloud-area").size() > 0;
                        const hasCloud = hasCloudInSvg || hasCloudInBody;
                        
                        if (hasCloud) {
                            // Remove cloud synchronously
                            if (svg.node()) {
                                svg.selectAll(".cloud-area").remove();
                                svg.selectAll("g.cloud-area").remove();
                                if (chartbody.node()) {
                                    chartbody.selectAll(".cloud-area").remove();
                                    chartbody.selectAll("g.cloud-area").remove();
                                }
                            }
                            removeCloudArea();
                            // If chart has scatter points, redraw to ensure clean state
                            const hasScatterPoints = chartHasRenderedPoints();
                            if (hasScatterPoints) {
                                drawDataElementsOnly(lastCapturedAggregates, props.cloud);
                            }
                        }
                    }
                }
            });
        }
    });

    // Grade update function
    const performGradeUpdate = async (gradeValue: number, selected: number[]): Promise<void> => {
        try {
            const { selectedClassName, selectedProjectId, selectedDatasetId } = persistantStore;
            const className = selectedClassName();
            const projectId = selectedProjectId();
            const datasetId = selectedDatasetId();

            if (!className || !projectId) {
                logError('Cannot update GRADE: missing class_name or project_id');
                return;
            }

            const response = await putData(`${apiEndpoints.admin.events}/tags`, {
                class_name: className,
                project_id: projectId,
                events: selected,
                event_types: ['BIN 10'],
                key: 'GRADE',
                value: gradeValue
            });

            if (response.success) {
                logDebug(`Successfully updated GRADE to ${gradeValue} for ${selected.length} event(s)`);

                // Update aggregates in HuniDB with new grade value
                try {
                    await huniDBStore.updateAggregatesMetadata(className, selected, { grade: gradeValue });
                    logDebug(`Successfully updated aggregates grade for ${selected.length} event(s) in HuniDB`);
                } catch (err) {
                    logError('Error updating aggregates grade in HuniDB:', err);
                }

                // Clear events cache to force refetch with updated tags
                if (datasetId) {
                    const datasetIdStr = String(datasetId);
                    const cacheKey = `events_${className}_${projectId}_${datasetIdStr}`;
                    unifiedDataStore.clearCacheForDataSource(cacheKey);
                    // Clear all events from cache and local state
                    unifiedDataStore.clearEvents().catch(err => {
                        logError('Error clearing events cache:', err);
                    });
                }

                if (props.onDataUpdate && typeof props.onDataUpdate === 'function') {
                    props.onDataUpdate();
                } else {
                    drawChart();
                }

                // Clear selection and selection banner after grade is posted (same as when grading maneuvers)
                clearSelection();
            } else {
                const data = response.data as { updated?: number; total?: number } | string | unknown[] | undefined;
                const msgFromData =
                    data != null && typeof data === 'object' && !Array.isArray(data) && typeof (data as { updated?: number }).updated === 'number' && typeof (data as { total?: number }).total === 'number'
                        ? `Partially updated: ${(data as { updated: number }).updated} of ${(data as { total: number }).total} events`
                        : typeof data === 'string'
                          ? data
                          : Array.isArray(data) && data.length > 0
                            ? (typeof data[0] === 'string' ? data[0] : JSON.stringify(data[0]))
                            : undefined;
                const errMsg = response.message ?? (response as { error?: string }).error ?? msgFromData;
                const hasStatus = response.status != null && response.status !== undefined;
                const errorMsg =
                    errMsg ||
                    (hasStatus
                        ? `HTTP ${response.status}: ${(response as { statusText?: string }).statusText || 'Unknown error'}`
                        : 'Network or server error (no details returned). Check that the admin server is reachable and try again.');
                logError(`Failed to update GRADE: ${errorMsg}`, {
                    status: response.status,
                    message: response.message,
                    error: (response as { error?: string }).error,
                    data: response.data,
                    url: `${apiEndpoints.admin.events}/tags`,
                    eventIds: selected,
                    ...(!errMsg && !hasStatus ? { fullResponse: response } : {})
                });
                // Show user-visible error
                alert(`Failed to update GRADE: ${errorMsg}\n\nPlease check the console for more details.`);
            }
        } catch (error: any) {
            logError('Error updating GRADE:', error);
            // Show user-visible error
            const errorMsg = error instanceof Error ? error.message : String(error);
            alert(`Error updating GRADE: ${errorMsg}\n\nPlease check the console for more details.`);
        }
    };

    const performStateUpdate = async (stateValue: string, selected: number[]): Promise<void> => {
        try {
            const { selectedClassName, selectedProjectId, selectedDatasetId } = persistantStore;
            const className = selectedClassName();
            const projectId = selectedProjectId();
            const datasetId = selectedDatasetId();

            if (!className || !projectId) {
                logError('Cannot update STATE: missing class_name or project_id');
                return;
            }

            const response = await putData(`${apiEndpoints.admin.events}/tags`, {
                class_name: className,
                project_id: projectId,
                events: selected,
                event_types: ['BIN 10'],
                key: 'FOILING_STATE',
                value: stateValue
            });

            if (response.success) {
                logDebug(`Successfully updated STATE to ${stateValue} for ${selected.length} event(s)`);

                // Update aggregates in HuniDB with new state value
                try {
                    await huniDBStore.updateAggregatesMetadata(className, selected, { state: stateValue });
                    logDebug(`Successfully updated aggregates state for ${selected.length} event(s) in HuniDB`);
                } catch (err) {
                    logError('Error updating aggregates state in HuniDB:', err);
                }

                // Clear events cache to force refetch with updated tags
                if (datasetId) {
                    const datasetIdStr = String(datasetId);
                    const cacheKey = `events_${className}_${projectId}_${datasetIdStr}`;
                    unifiedDataStore.clearCacheForDataSource(cacheKey);
                    // Clear all events from cache and local state
                    unifiedDataStore.clearEvents().catch(err => {
                        logError('Error clearing events cache:', err);
                    });
                }

                if (props.onDataUpdate && typeof props.onDataUpdate === 'function') {
                    props.onDataUpdate();
                } else {
                    drawChart();
                }

                // Clear selection and selection banner after state is posted (same as when grading maneuvers)
                clearSelection();
            } else {
                const data = response.data as { updated?: number; total?: number } | string | unknown[] | undefined;
                const msgFromData =
                    data != null && typeof data === 'object' && !Array.isArray(data) && typeof (data as { updated?: number }).updated === 'number' && typeof (data as { total?: number }).total === 'number'
                        ? `Partially updated: ${(data as { updated: number }).updated} of ${(data as { total: number }).total} events`
                        : typeof data === 'string'
                          ? data
                          : Array.isArray(data) && data.length > 0
                            ? (typeof data[0] === 'string' ? data[0] : JSON.stringify(data[0]))
                            : undefined;
                const errMsg = response.message ?? (response as { error?: string }).error ?? msgFromData;
                const hasStatus = response.status != null && response.status !== undefined;
                const errorMsg =
                    errMsg ||
                    (hasStatus
                        ? `HTTP ${response.status}: ${(response as { statusText?: string }).statusText || 'Unknown error'}`
                        : 'Network or server error (no details returned). Check that the admin server is reachable and try again.');
                logError(`Failed to update STATE: ${errorMsg}`, {
                    status: response.status,
                    message: response.message,
                    error: (response as { error?: string }).error,
                    data: response.data,
                    url: `${apiEndpoints.admin.events}/tags`,
                    eventIds: selected,
                    ...(!errMsg && !hasStatus ? { fullResponse: response } : {})
                });
                // Show user-visible error
                alert(`Failed to update STATE: ${errorMsg}\n\nPlease check the console for more details.`);
            }
        } catch (error: any) {
            logError('Error updating STATE:', error);
            // Show user-visible error
            const errorMsg = error instanceof Error ? error.message : String(error);
            alert(`Error updating STATE: ${errorMsg}\n\nPlease check the console for more details.`);
        }
    };

    // Lifecycle hooks
    onMount(() => {
        const capturedAggregates = props.aggregates;
        const capturedCloud = props.cloud;
        lastCapturedAggregates = capturedAggregates;
        lastCapturedCloud = capturedCloud;
        
        if (chartRef) {
            intersectionObserver = new IntersectionObserver(
                (entries) => {
                    entries.forEach(entry => {
                        const wasVisible = isVisible();
                        setIsVisible(entry.isIntersecting);
                        
                        // Manual viewport check to verify actual visibility
                        const rect = chartRef?.getBoundingClientRect();
                        const isActuallyVisible = rect && rect.top < window.innerHeight && rect.bottom > 0 && rect.width > 0 && rect.height > 0;
                        
                        if (entry.isIntersecting && !wasVisible) {
                            tryAttachScatterListeners();
                            requestAnimationFrame(() => tryAttachScatterListeners());
                        } else if (!entry.isIntersecting && wasVisible) {
                            // Only remove elements if chart is actually not visible (double-check with manual viewport check)
                            if (!isActuallyVisible) {
                                const scatterPoints = d3.select(chartRef).selectAll(".scatter");
                                scatterPoints
                                    .on("mouseover", null)
                                    .on("mouseout", null)
                                    .on("click", null)
                                    .remove();
                                d3.select(chartRef).selectAll(".target-line, .target-path, .tgt, .fit, .bl").remove();
                                d3.select(chartRef).selectAll(".cloud-area").remove();
                                d3.select(chartRef).selectAll(".lasso").remove();
                                // Canvas mode: drop the canvas layer + hit rect alongside the SVG points.
                                teardownCanvasScatterInteraction();
                                removeCanvasElement();
                                isHighDensityMode = false;
                                canvasScatterData = [];
                                listenersAttached = false;
                            }
                        }
                        
                        if (entry.isIntersecting || isActuallyVisible) {
                            const hasSVG = d3.select(chartRef).select("svg").node();
                            if (!hasSVG) {
                                // Use current props instead of captured values to ensure we have latest data
                                drawChart(props.aggregates, props.cloud);
                            } else {
                                // Only check cloud state when visibility actually changes, or throttle frequent checks
                                const now = Date.now();
                                const visibilityChanged = entry.isIntersecting !== wasVisible;
                                const shouldCheckCloud = visibilityChanged || (now - lastCloudCheckTime > CLOUD_CHECK_THROTTLE_MS);
                                
                                if (shouldCheckCloud) {
                                    lastCloudCheckTime = now;
                                    // Use current props instead of captured values to ensure we have latest data
                                    const currentAggregates = props.aggregates;
                                    const currentCloud = props.cloud;
                                    const hasDataElements = chartHasRenderedPoints();
                                    const hasCloud = d3.select(chartRef).selectAll(".cloud-area").size() > 0;
                                    const shouldHaveCloud = currentCloud && Array.isArray(currentCloud) && currentCloud.length > 0;
                                    
                                    // Only check cloud state if cloud data should exist
                                    if (!hasDataElements) {
                                        drawDataElementsOnly(currentAggregates, currentCloud);
                                    } else if (shouldHaveCloud) {
                                        // Only check cloud state if we expect cloud to be present
                                        if (!hasCloud) {
                                            drawDataElementsOnly(currentAggregates, currentCloud);
                                        }
                                    } else if (hasCloud) {
                                        // Cloud exists but shouldn't - remove it without full redraw
                                        d3.select(chartRef).selectAll(".cloud-area").remove();
                                    }
                                }
                            }
                        }
                    });
                },
                {
                    // Smaller buffer than before — 500px caused too many off-screen charts to draw at once on performance pages.
                    rootMargin: '200px 0px 200px 0px',
                    threshold: 0.01
                }
            );
            intersectionObserver.observe(chartRef);
            
            setTimeout(() => {
                if (isVisible() && !d3.select(chartRef).select("svg").node()) {
                    drawChart(props.aggregates, props.cloud);
                }
            }, 0);
        } else {
            drawChart(props.aggregates, props.cloud);
        }

        let resizeTimeout: ReturnType<typeof setTimeout> | null = null;
        const handleResize = () => {
            if (!isVisible()) return;
            if (resizeTimeout) {
                clearTimeout(resizeTimeout);
            }
            resizeTimeout = setTimeout(() => {
                drawChart(lastCapturedAggregates, lastCapturedCloud);
            }, 150);
        };

        window.addEventListener('resize', handleResize);

        const handleKeyPress = async (event: KeyboardEvent) => {
            try {
                const target = event.target as HTMLElement | null;
                if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
                    return;
                }

                if (typeof document !== 'undefined' && document.querySelector('.pagesettings-modal')) {
                    return;
                }

                const key = event.key;
                const colorType = colorModeForHotkeys();
                const root = typeof document !== "undefined" ? document.documentElement : null;

                // SELECTIONS digit: set lasso-style busy UI synchronously, then end this task so the browser
                // can paint before assign() / assignLocalPerformanceSelection (large sync scans on main thread).
                const selectionsDigitWillAssign =
                    colorType === 'SELECTIONS' &&
                    /^[0-9]$/.test(key) &&
                    typeof props.onLocalSelectionAssign === "function";
                if (selectionsDigitWillAssign) {
                    if (!scatterSelectionsDigitThisChartMayHandle(scatterSelectionsDigitHotkeyRootEl, chartRef)) {
                        return;
                    }
                    clearSelectionsHotkeyBannerAndTimer();
                    setIsComputingLassoSelection(true);
                    setLassoComputingCursor(true);
                    if (root) {
                        root.classList.add(PERFORMANCE_SELECTION_HOTKEY_BUSY_CLASS);
                        void root.offsetHeight;
                    }
                    event.preventDefault();
                    event.stopPropagation();
                    event.stopImmediatePropagation();
                    const keyDigit = parseInt(key, 10);
                    const onAssign = props.onLocalSelectionAssign;
                    requestAnimationFrame(() => {
                        // Prefer IntersectionObserver; allow SELECTIONS and grade/state hotkeys when chart is in view
                        // but observer has not set isVisible yet (first paint / cross-page navigation).
                        if (!isVisible()) {
                            const colorSupportsHotkeys =
                                colorType === 'SELECTIONS' ||
                                colorType === 'STATE' ||
                                colorType === 'GRADE';
                            const allowWithoutObserver =
                                colorSupportsHotkeys &&
                                isScatterChartInViewport();
                            if (!allowWithoutObserver) {
                                clearPerformanceSelectionHotkeyBusy();
                                clearSelectionsHotkeyBannerAndTimer();
                                setIsComputingLassoSelection(false);
                                setLassoComputingCursor(false);
                                return;
                            }
                        }
                        const selected = domainSelectedEvents();
                        if (typeof onAssign === 'function' && Array.isArray(selected) && selected.length > 0) {
                            if (selectionsHotkeyBannerClearTimer != null) {
                                clearTimeout(selectionsHotkeyBannerClearTimer);
                                selectionsHotkeyBannerClearTimer = null;
                            }
                            try {
                                onAssign(selected, keyDigit);
                                setDomainSelectedEvents([]);
                                setHasSelection(false);
                                setTriggerSelection(true);
                            } finally {
                                scheduleClearPerformanceSelectionHotkeyAfterAssign();
                            }
                        } else {
                            clearPerformanceSelectionHotkeyBusy();
                            setIsComputingLassoSelection(false);
                            setLassoComputingCursor(false);
                            if (selectionsHotkeyBannerClearTimer != null) {
                                clearTimeout(selectionsHotkeyBannerClearTimer);
                            }
                            setSelectionsHotkeyBanner('Select points on the chart first');
                            selectionsHotkeyBannerClearTimer = setTimeout(() => {
                                selectionsHotkeyBannerClearTimer = null;
                                setSelectionsHotkeyBanner(null);
                            }, 1400);
                        }
                    });
                    return;
                }

                // Prefer IntersectionObserver; allow SELECTIONS and grade/state hotkeys when chart is in view
                // but observer has not set isVisible yet (first paint / cross-page navigation).
                if (!isVisible()) {
                    const colorSupportsHotkeys =
                        colorType === 'SELECTIONS' ||
                        colorType === 'STATE' ||
                        colorType === 'GRADE';
                    const allowWithoutObserver =
                        colorSupportsHotkeys &&
                        isScatterChartInViewport();
                    if (!allowWithoutObserver) {
                        return;
                    }
                }

                const selected = domainSelectedEvents();

                if (shouldBlockGradeLikeHotkeysForPerformanceSelections(key)) {
                    return;
                }

                if (isUpdatingGrade()) {
                    event.preventDefault();
                    event.stopPropagation();
                    return;
                }

                const now = Date.now();
                if (now - lastGradeUpdateTime < 1000) {
                    event.preventDefault();
                    event.stopPropagation();
                    return;
                }

                // Only handle hotkeys when colored by STATE or GRADE
                if (colorType !== 'STATE' && colorType !== 'GRADE') {
                    return;
                }

                const currentUser = user();
                if (!currentUser) return;

                // Superusers can always grade
                if (!currentUser.is_super_user) {
                    // Check if user is a reader - readers cannot grade
                    const userPermissions = currentUser.permissions;
                    let isReader = false;
                    
                    if (typeof userPermissions === 'string') {
                        isReader = userPermissions === 'reader';
                    } else if (typeof userPermissions === 'object' && userPermissions !== null) {
                        const permissionValues = Object.values(userPermissions);
                        // User is a reader if ALL their permissions are 'reader'
                        isReader = permissionValues.length > 0 && permissionValues.every(p => p === 'reader');
                    }
                    
                    if (isReader) {
                        return; // Readers cannot grade
                    }
                }

                // Handle STATE update hotkeys (0, 1, 2) when colored by STATE
                if (colorType === 'STATE' && selected && selected.length > 0 && ['0', '1', '2'].includes(key)) {
                    event.preventDefault();
                    event.stopPropagation();
                    event.stopImmediatePropagation();

                    lastGradeUpdateTime = now;
                    setIsUpdatingGrade(true);

                    // Map keys to state values
                    const stateMap: Record<string, string> = { '0': 'H0', '1': 'H1', '2': 'H2' };
                    const stateValue = stateMap[key];

                    const message = `Are you sure you want to update STATE to ${stateValue} for ${selected.length} selected event(s)?\n\nThis action will modify the data and cannot be easily undone.`;
                    const confirmed = window.confirm(message);
                    if (!confirmed) {
                        setIsUpdatingGrade(false);
                        return;
                    }

                    setTimeout(() => {
                        performStateUpdate(stateValue, selected)
                            .catch(error => {
                                logError('Error in performStateUpdate:', error);
                            })
                            .finally(() => {
                                setIsUpdatingGrade(false);
                            });
                    }, 0);
                    return;
                }

                // Handle GRADE update hotkeys (0, 1, 2, 3) when colored by GRADE
                if (colorType === 'GRADE' && selected && selected.length > 0 && ['0', '1', '2', '3'].includes(key)) {
                    event.preventDefault();
                    event.stopPropagation();
                    event.stopImmediatePropagation();

                    lastGradeUpdateTime = now;
                    setIsUpdatingGrade(true);

                    const gradeValue = parseInt(key, 10);
                    const message = `Are you sure you want to update GRADE to ${gradeValue} for ${selected.length} selected event(s)?\n\nThis action will modify the data and cannot be easily undone.`;
                    const confirmed = window.confirm(message);
                    if (!confirmed) {
                        setIsUpdatingGrade(false);
                        return;
                    }

                    setTimeout(() => {
                        performGradeUpdate(gradeValue, selected)
                            .catch(error => {
                                logError('Error in performGradeUpdate:', error);
                            })
                            .finally(() => {
                                setIsUpdatingGrade(false);
                            });
                    }, 0);
                    return;
                }
            } catch (error: any) {
                logError('Error in handleKeyPress:', error);
                event.preventDefault();
                event.stopPropagation();
                setIsUpdatingGrade(false);
                clearPerformanceSelectionHotkeyBusy();
                clearSelectionsHotkeyBannerAndTimer();
                setIsComputingLassoSelection(false);
                setLassoComputingCursor(false);
            }
        };

        window.addEventListener('keydown', handleKeyPress, true);

        // onCleanup, not a returned function: Solid discards onMount return values, so these
        // window listeners used to accumulate one set per mount and the hotkeys fired N times.
        onCleanup(() => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('keydown', handleKeyPress, true);
            if (resizeTimeout) {
                clearTimeout(resizeTimeout);
            }
        });
    });

    onCleanup(() => {
        clearPerformanceSelectionHotkeyBusy();
        clearSelectionsHotkeyBannerAndTimer();
        // Set cleanup flag immediately to prevent any pending operations
        isCleaningUp = true;
        
        if (intersectionObserver && chartRef) {
            intersectionObserver.unobserve(chartRef);
            intersectionObserver.disconnect();
            intersectionObserver = null;
        }
        
        if (filterRedrawTimer) {
            clearTimeout(filterRedrawTimer);
            filterRedrawTimer = null;
        }
        if (initialDrawTimer) {
            clearTimeout(initialDrawTimer);
            initialDrawTimer = null;
        }
        cancelZoomFitWork();
        if (pendingFitTimerId != null) {
            clearTimeout(pendingFitTimerId);
            pendingFitTimerId = null;
        }
        if (triggerSelectionTimer) {
            clearTimeout(triggerSelectionTimer);
            triggerSelectionTimer = null;
        }
        if (selectionUpdateTimer) {
            clearTimeout(selectionUpdateTimer);
            selectionUpdateTimer = null;
        }
        
        if (lassoWorker) {
            lassoWorker.terminate();
            lassoWorker = null;
        }
        
        d3.select(chartRef).selectAll("*").remove();
        isLassoActive = false;
        isComputingSelection = false;
        setIsComputingLassoSelection(false);
        setLassoComputingCursor(false);
        setColorReloadCursor(false);
        if (scatterSelectionsDigitHotkeyRootEl) {
            scatterSelectionsDigitClearPointerOwnerRootIfMatch(scatterSelectionsDigitHotkeyRootEl);
        }
    });

    const infoType = () => props.infoType ?? "";
    const infoMessage = () => (props.infoMessage ?? "").trim();
    const showInfoIcon = () => infoType() === "info" && infoMessage().length > 0;
    const showWarningIcon = () => infoType() === "warning" && infoMessage().length > 0;
    const showInfoOrWarning = () => showInfoIcon() || showWarningIcon();

    const INFO_TOOLTIP_OFFSET = 15;
    const INFO_TOOLTIP_MAX_WIDTH = 280; // match .advanced-scatter-info-tooltip max-width in CSS
    const INFO_TOOLTIP_SHIFT_LEFT = INFO_TOOLTIP_MAX_WIDTH / 2; // translate half the width when near edge

    const getInfoTooltipPosition = (e: MouseEvent) => {
        try {
            if (!containerRef) {
                const x = e.clientX + INFO_TOOLTIP_OFFSET;
                const y = e.clientY + INFO_TOOLTIP_OFFSET;
                const shiftLeft = x + INFO_TOOLTIP_MAX_WIDTH > window.innerWidth;
                return {
                    x: shiftLeft ? e.clientX - INFO_TOOLTIP_SHIFT_LEFT - INFO_TOOLTIP_OFFSET : x,
                    y,
                };
            }
            const rect = containerRef.getBoundingClientRect();
            const scrollX = containerRef.scrollLeft ?? 0;
            const scrollY = containerRef.scrollTop ?? 0;
            const relX = e.clientX - rect.left + scrollX;
            const relY = e.clientY - rect.top + scrollY;
            const xDefault = relX + INFO_TOOLTIP_OFFSET;
            const rightEdge = scrollX + rect.width;
            const shiftLeft = xDefault + INFO_TOOLTIP_MAX_WIDTH > rightEdge;
            const x = shiftLeft ? relX - INFO_TOOLTIP_SHIFT_LEFT - INFO_TOOLTIP_OFFSET : xDefault;
            return { x, y: relY + INFO_TOOLTIP_OFFSET };
        } catch {
            return { x: e.clientX + INFO_TOOLTIP_OFFSET, y: e.clientY + INFO_TOOLTIP_OFFSET };
        }
    };

    const onInfoIconMouseEnter = (e: MouseEvent) => {
        const msg = infoMessage();
        if (msg.length === 0) return;
        const position = getInfoTooltipPosition(e);
        const escaped = msg.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
        setTooltip({
            visible: true,
            content: `<span class="advanced-scatter-info-tooltip">${escaped}</span>`,
            x: position.x,
            y: position.y,
        });
    };
    const onInfoIconMouseMove = (e: MouseEvent) => {
        if (infoMessage().length === 0) return;
        const position = getInfoTooltipPosition(e);
        setTooltip((prev) => (prev.visible ? { ...prev, x: position.x, y: position.y } : prev));
    };
    const onInfoIconMouseLeave = () => {
        setTooltip({ visible: false, content: "", x: 0, y: 0 });
    };

    const onScatterSelectionsDigitHotkeyRootPointerEnter = (e: PointerEvent) => {
        scatterSelectionsDigitSetPointerOwnerRoot(e.currentTarget as HTMLElement);
    };
    const onScatterSelectionsDigitHotkeyRootPointerLeave = (e: PointerEvent) => {
        const root = e.currentTarget as HTMLElement;
        const rel = e.relatedTarget as Node | null;
        if (rel && root.contains(rel)) return;
        scatterSelectionsDigitClearPointerOwnerRootIfMatch(root);
    };

    return (
        <div
            class="advanced-scatter-selections-digit-hotkey-root"
            style={{ width: "100%", height: "100%", display: "flex", position: "relative" }}
            ref={(el) => {
                scatterSelectionsDigitHotkeyRootEl = el as HTMLDivElement | null;
            }}
            onPointerEnter={onScatterSelectionsDigitHotkeyRootPointerEnter}
            onPointerLeave={onScatterSelectionsDigitHotkeyRootPointerLeave}
        >
            <div
                ref={(el) => { chartRef = el as HTMLDivElement }}
                class={`advanced-scatter-chart-container ${props.zoom ? 'advanced-scatter-chart-container-zoomed' : 'advanced-scatter-chart-container-normal'}`}
                classList={{ "advanced-scatter-highlight-lines": performanceDisplayStore.highlightScatterLines() }}
            ></div>
            <Show when={showInfoOrWarning()}>
                <div
                    class="advanced-scatter-info-icon-wrap"
                    role="img"
                    aria-label={infoType() === "warning" ? "Warning" : "Info"}
                    onMouseEnter={onInfoIconMouseEnter}
                    onMouseMove={onInfoIconMouseMove}
                    onMouseLeave={onInfoIconMouseLeave}
                >
                    <Show when={showInfoIcon()}>
                        <img
                            src={infoIconUrl}
                            alt="Info"
                            class="advanced-scatter-info-icon"
                            classList={{ "advanced-scatter-info-icon-dark": isDark() }}
                        />
                    </Show>
                    <Show when={showWarningIcon()}>
                        <img
                            src={warningIconUrl}
                            alt="Warning"
                            class="advanced-scatter-info-icon"
                        />
                    </Show>
                </div>
            </Show>
            <Show when={isComputingLassoSelection() || !!selectionsHotkeyBanner()}>
                <div
                    class="advanced-scatter-selection-status-banner"
                    role="status"
                    aria-live="polite"
                >
                    {isComputingLassoSelection() ? 'Computing selection...' : selectionsHotkeyBanner()}
                </div>
            </Show>
            <Show when={props.zoom && tableData().length > 0}>
                <div class="advanced-scatter-table-container">
                    <Table
                        xaxis={props.xaxis}
                        fitData={tableData()}
                        version={fitDataVersion()}
                        loessFactor={loessBandwidth()}
                        // Smoothness only applies to fits; the reference curves are fixed.
                        onLoessFactorChange={referenceTableMode() ? undefined : (delta: number) => {
                            setLoessBandwidth((v) => {
                                const next = clampLoessBandwidth(v + delta);
                                lastLoessBandwidth = next;  // survives the zoom-toggle remount
                                return next;
                            });
                            drawFit();
                        }}
                    />
                </div>
            </Show>
            <div style={{ clear: "both" }}></div>
        </div>
    );
}

